<template>
  <div class="ticket-form-container" :class="{ 'admin-view': esAdmin }">
    <!-- Contenedor principal con tabla y botón -->
    <div class="main-content">
      <!-- Header con botón de nuevo ticket y estadísticas -->
      <div class="table-header">
        <h3 v-if="!showTicketDetail">📋 Tickets Generados</h3>
        <div v-else class="detail-header">
          <button class="back-btn" @click="cerrarDetalle">
            ← Volver a la Lista
          </button>
          <h3>Detalle del Ticket #{{ ticketDetalle?.id }}</h3>
        </div>
        <div class="header-actions" v-if="!showTicketDetail">
          <button class="new-ticket-btn" @click="abrirFormulario">
            ➕ Nuevo Ticket
          </button>
          
          <div v-if="esAdminOTecnico" class="admin-actions">
            <button
              type="button"
              class="stats-btn stats-toggle-btn"
              @click="toggleAccionesEstadisticas"
              :aria-expanded="mostrarAccionesEstadisticas.toString()"
            >
              📊 Estadísticas
              <span class="dropdown-arrow" :class="{ open: mostrarAccionesEstadisticas }">▼</span>
            </button>
            <transition name="fade">
              <div
                v-if="mostrarAccionesEstadisticas"
                class="stats-buttons"
              >
                <button class="stats-option-btn" @click="verTicketsCreadosResueltos">📊 Tickets Creados y Resueltos</button>
                <button class="stats-option-btn" @click="verTiempoPromedio">⏱️ Tiempo Promedio</button>
                <button class="stats-option-btn" @click="verTicketsPorTecnico">👥 Tickets por Técnico</button>
                <button class="stats-option-btn" @click="verTicketsPorArea">🏥 Tickets por Área</button>
		<button class="stats-option-btn" @click="verProblemasMasComunes">🔍 Problemas Más Comunes</button>
              </div>
            </transition>
          </div>
        </div>
      </div>
    
      <!-- Contenido principal: Tabla o Detalle -->
      <div class="ticket-list" v-if="!showTicketDetail">
        <!-- Filtro por área -->
        <div class="filter-section">
          <div class="filter-controls">
            <div class="filter-group autocomplete-container">
              <v-autocomplete
                v-model="filtroArea"
                :items="areasDisponibles"
                item-text="nombre"
                item-value="nombre"
                :filter="filtrarAreas"
                label="🔍 Filtrar por Área"
                placeholder="Selecciona un área"
                clearable
                dense
                outlined
                hide-details
                class="area-autocomplete"
              >
                <template v-slot:prepend-item>
                  <v-list-item @click="filtroArea = ''">
                    <v-list-item-content>
                      <v-list-item-title>📋 Todas las áreas</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider class="mt-2"></v-divider>
                </template>
              </v-autocomplete>
            </div>
            <div v-if="esAdminOTecnico" class="filter-group">
              <label for="prioridadFilter">🚨 Filtrar por Prioridad:</label>
              <select id="prioridadFilter" v-model="filtroPrioridad" class="area-filter">
                <option value="">Todas las prioridades</option>
                <option value="ALTA">🔴 Alta</option>
                <option value="MEDIA">🟡 Media</option>
                <option value="BAJA">🟢 Baja</option>
              </select>
            </div>
            <!-- Filtro de estado (solo para admin/tecnico) -->
            <div v-if="esAdminOTecnico" class="filter-group">
              <label for="filtroEstado">Estado:</label>
              <select id="filtroEstado" v-model="filtroEstado" class="area-filter">
                <option value="activos">🟢 Activos</option>
                <option value="resueltos">🔵 Resueltos</option>
                <option value="cancelados">🔴 Cancelados</option>
                <option value="todos">📈 Todos</option>
              </select>
            </div>

            <button v-if="filtroArea || (esAdminOTecnico && filtroPrioridad) || filtroEstado !== 'activos'" @click="limpiarFiltros" class="clear-filter-btn">
              ❌ Limpiar filtros
            </button>

          </div>
          <div class="filter-info">
            <span v-if="filtroArea || (esAdminOTecnico && filtroPrioridad)">
              Mostrando tickets de: 
              <strong v-if="filtroArea">{{ filtroArea }}</strong>
              <span v-if="filtroArea && (esAdminOTecnico && filtroPrioridad)">, </span>
              <strong v-if="esAdminOTecnico && filtroPrioridad">{{ filtroPrioridad }}</strong>
            </span>
            <span v-else>Mostrando todos los tickets</span>
            <span class="ticket-count">({{ ticketsFiltrados.length }} tickets)</span>
          </div>
        </div>
        
        <table>
        <thead>
          <tr>
            <th v-if="esAdminOTecnico" class="folio-header">ID</th>
            <th v-else class="folio-header">Folio</th>
            <th>Título</th>
            <th v-if="esAdminOTecnico">Solicitante</th>
            <th>Área Solicitante</th>
            <th>Prioridad</th>
            <th>Fecha Creación</th>
            <th>Fecha Cierre</th>
            <th>Técnico</th>
            <th>Estado</th>
          </tr>
        </thead>
        <tbody>
                      <tr v-for="ticket in ticketsFiltrados" :key="ticket.id" @click="abrirDetalle(ticket)" class="ticket-row" :style="getPrioridadStyle(ticket)">
              <td v-if="esAdminOTecnico" class="folio-cell">{{ ticket.id }}</td>
              <td v-else class="folio-cell">{{ generarFolio(ticket) }}</td>
              <td>{{ ticket.titulo }}</td>
              <td v-if="esAdminOTecnico">{{ ticket.nombre }}</td>
              <td>{{ ticket.areaSolicita }}</td>
              <td v-if="esAdminOTecnico" @click.stop>
                <select 
                  v-model="ticket.prioridad" 
                  @change="cambiarPrioridadManual(ticket)" 
                  class="inline-select"
                  :disabled="ticket.esCancelado || ticket.esResuelto">
                  <option value="ALTA">🟠 Alta</option>
                  <option value="MEDIA">🟡 Media</option>
                  <option value="BAJA">🟢 Baja</option>
                </select>
              </td>
              <td v-else>
                <span class="prioridad-badge" :style="getPrioridadBadgeStyle(ticket)">
                  {{ ticket.prioridad || 'Media' }}
                  <span v-if="ticket.prioridadManual" class="manual-indicator" title="Prioridad establecida manualmente por administrador">👤</span>
                </span>
              </td>
              <td class="fecha-hora-cell">
                <div class="fecha-line">{{ formatearFechaHoraCorta(ticket.fechaCreacion).fecha }}</div>
                <div class="hora-line">{{ formatearFechaHoraCorta(ticket.fechaCreacion).hora }}</div>
              </td>
              <td class="fecha-hora-cell">
                <div v-if="ticket.fechaCierre && ticket.fechaCierre !== ''" class="fecha-line">{{ formatearFechaHoraCorta(ticket.fechaCierre).fecha }}</div>
                <div v-if="ticket.fechaCierre && ticket.fechaCierre !== ''" class="hora-line">{{ formatearFechaHoraCorta(ticket.fechaCierre).hora }}</div>
                <div v-else class="fecha-line text-muted">Pendiente</div>
              </td>
              <td v-if="esAdminOTecnico" @click.stop>
                <select 
                  v-model="ticket.tecnicoAsignadoId" 
                  @change="actualizarTecnico(ticket)" 
                  class="inline-select"
                  :disabled="ticket.esCancelado || ticket.esResuelto">
                  <option :value="null">Sin asignar</option>
                  <option v-for="tecnico in listaTecnicos" :key="tecnico.id" :value="tecnico.id">{{ tecnico.nombre }}</option>
                </select>
              </td>
              <td v-else>
                {{ ticket.tecnico || 'Sin asignar' }}
              </td>
              <td v-if="esAdminOTecnico" @click.stop>
                <select 
                  v-model="ticket.estado" 
                  @focus="guardarEstadoActual(ticket)"
                  @change="actualizarEstado(ticket)" 
                  class="inline-select"
                  :disabled="ticket.esCancelado || ticket.esResuelto">
                  <option value="PENDIENTE">Pendiente</option>
                  <option value="ASIGNADO">Asignado</option>
                  <option value="EN_PROCESO"> En Proceso</option>
                  <option value="REASIGNAR">Reasignar</option>
                  <option value="RESUELTO"> Resuelto</option>
                </select>
              </td>
              <td v-else>
                <span class="estado-badge" :class="getEstadoClass(ticket.estado)">
                  {{ getEstadoText(ticket.estado, esAdmin) }}
                </span>
              </td>
            </tr>
        </tbody>
      </table>
      </div>

      <!-- Panel de detalle del ticket -->
      <div v-if="showTicketDetail && ticketDetalle" class="ticket-detail-panel">
        <div class="detail-content">
          <!-- Card única con toda la información -->
          <div class="single-detail-card">
            <div class="card-header">
              <div class="header-left">
                <h2>Detalle del Ticket {{ generarFolio(ticketDetalle) }}</h2>
                <div class="status-summary">
                  <span class="detail-badge detail-badge-prioridad" :style="getPrioridadBadgeStyle(ticketDetalle)">{{ ticketDetalle.prioridad || 'Media' }}</span>
                  <span class="detail-badge detail-badge-estado" :class="getEstadoClass(ticketDetalle.estado)">{{ getEstadoText(ticketDetalle.estado, esAdmin) }}</span>
                </div>
              </div>
              <div class="header-actions">
                
                <!-- Botón de cancelar para usuarios (validación en backend) -->
                <button 
                  @click="abrirModalCancelacion(ticketDetalle)" 
                  class="cancel-ticket-btn-detail"
                  title="Cancelar este ticket"
                >
                  ❌ Cancelar Ticket
                </button>
                
                <!-- Botones de descarga para usuarios (solo reporte normal) -->
                <button 
                  v-if="!esAdminOTecnico && ticketDetalle.estado === 'resuelto'" 
                  @click="descargarTicketNormal" 
                  class="download-btn" 
                  title="Descargar reporte del ticket"
                >
                  📄 Descargar Reporte
                </button>
                
                <!-- Botones de descarga para administradores y técnicos -->
                <div v-if="esAdminOTecnico" class="admin-download-buttons">
                  <button 
                    @click="descargarTicketNormal" 
                    class="download-btn normal-report" 
                    title="Descargar reporte normal"
                  >
                    📄 Reporte Normal
                  </button>
                  <button 
                    @click="descargarTicketCompleto" 
                    class="download-btn complete-report" 
                    title="Descargar reporte completo con todas las soluciones"
                  >
                    📋 Reporte Completo
                  </button>
                </div>
                <button @click="cerrarDetalle" class="close-detail-btn" title="Cerrar">
                  ✕
                </button>
              </div>
            </div>
            
            <div class="card-content">
              <!-- Diseño en filas organizadas -->
              
              <!-- Sección 1: Información Principal -->
              <div class="detail-section">
                <div class="section-header">
                  <h3> Información Principal</h3>
                </div>
                <div class="section-content">
                  <div class="detail-form-group">
                    <label>Título del Problema</label>
                    <div class="detail-value title-value">{{ ticketDetalle.titulo || 'Sin título' }}</div>
                  </div>
                  
                  <div class="detail-form-row">
                    <div class="detail-form-group">
                      <label>Descripción Detallada</label>
                      <div class="detail-value description-value">{{ ticketDetalle.descripcion || 'Sin descripción' }}</div>
                    </div>
                    
                    <div v-if="ticketDetalle.notas" class="detail-form-group">
                      <label> Notas y Comentarios</label>
                      <div class="detail-value">{{ ticketDetalle.notas }}</div>
                    </div>
                  </div>
                  
                  <div v-if="ticketDetalle.comentariosInternos" class="detail-form-group">
                    <label> Comentarios Internos</label>
                    <div class="detail-value">{{ ticketDetalle.comentariosInternos }}</div>
                  </div>
                </div>
              </div>
              
              <!-- Sección 2: Información del Solicitante y Ubicación -->
              <div class="detail-section">
                <div class="section-header">
                  <h3>👤 Información del Solicitante</h3>
                </div>
                <div class="section-content">
              <div class="detail-form-row">
                <div class="detail-form-group">
                  <label> Solicitante</label>
                  <div class="detail-value">{{ ticketDetalle.nombre || 'No especificado' }}</div>
                </div>
                <div class="detail-form-group">
                  <label> Área que Solicita</label>
                  <div class="detail-value">{{ ticketDetalle.areaSolicita || 'No especificado' }}</div>
                </div>
                <div class="detail-form-group">
                  <label>Ubicación</label>
                  <div class="detail-value">{{ ticketDetalle.ubicacion || 'No especificado' }}</div>
                </div>
              </div>
              </div>
              </div>
              
              <!-- Sección 3: Información Técnica -->
              <div class="detail-section">
                <div class="section-header">
                  <h3>🔧 Información Técnica</h3>
                </div>
                <div class="section-content">
                  <div class="detail-form-row">
                    <div class="detail-form-group">
                      <label>Área Requerida</label>
                      <div class="detail-value">{{ getSolicitudText(ticketDetalle.solicitudArea) || 'No especificado' }}</div>
                    </div>
                <div v-if="ticketDetalle.equipo" class="detail-form-group">
                  <label>Equipo Afectado</label>
                  <div class="detail-value">{{ ticketDetalle.equipo }}</div>
                  <div v-if="ticketDetalle.serialNumber" class="detail-sub-value">
                    <small>🏷️ Serie: {{ ticketDetalle.serialNumber }}</small>
                  </div>
                  <div v-if="ticketDetalle.descripcionEquipo" class="detail-sub-value">
                    <small> {{ ticketDetalle.descripcionEquipo }}</small>
                  </div>
                </div>
                <div v-if="ticketDetalle.insumos" class="detail-form-group">
                  <label>📦 Insumos Requeridos</label>
                  <div class="detail-value">{{ ticketDetalle.insumos }}</div>
                  <div v-if="ticketDetalle.cantidadInsumos" class="detail-sub-value">
                    <small> Cantidad: {{ ticketDetalle.cantidadInsumos }}</small>
                  </div>
                  <div v-if="ticketDetalle.descripcionInsumos" class="detail-sub-value">
                    <small> {{ ticketDetalle.descripcionInsumos }}</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Sección 4: Estado y Seguimiento -->
              <div class="detail-section">
                <div class="section-header">
                  <h3>Estado y Seguimiento</h3>
                </div>
                <div class="section-content">
                  <div class="status-badges-row">
                    <div class="status-badge-item">
                      <label>Prioridad</label>
                      <span class="detail-badge detail-badge-prioridad" :style="getPrioridadBadgeStyle(ticketDetalle)">
                        {{ ticketDetalle.prioridad || 'Media' }}
                        <span v-if="ticketDetalle.prioridadManual" class="manual-indicator" title="Prioridad establecida manualmente por administrador">👤</span>
                      </span>
                    </div>
                    <div class="status-badge-item">
                      <label>Estado</label>
                      <span class="detail-badge detail-badge-estado" :class="getEstadoClass(ticketDetalle.estado)">{{ getEstadoText(ticketDetalle.estado, esAdmin) }}</span>
                </div>
              </div>
              
              <div class="detail-form-row">
                <div class="detail-form-group">
                  <label>👨‍💼 Técnico Asignado</label>
                  <div class="detail-value" :class="{ 'empty': !ticketDetalle.tecnico }">{{ ticketDetalle.tecnico || 'Sin asignar' }}</div>
                </div>
                <div class="detail-form-group">
                  <label>📅 Fecha de Creación</label>
                  <div class="detail-value">{{ formatearFecha(ticketDetalle.fechaCreacion) }}</div>
                </div>
                <div class="detail-form-group">
                  <label>✅ Fecha de Cierre</label>
                <div class="detail-value" :class="{ 'empty': !ticketDetalle.fechaCierre || !esFechaPosterior(ticketDetalle.fechaCreacion, ticketDetalle.fechaCierre) }">
                  {{ ticketDetalle.fechaCierre && esFechaPosterior(ticketDetalle.fechaCreacion, ticketDetalle.fechaCierre) ? formatearFecha(ticketDetalle.fechaCierre) : 'Sin cerrar' }}
                </div>
                </div>
                <div class="detail-form-group">
                  <label>⏱ Tiempo de Resolución</label>
                  <div class="detail-value">
                    {{ ticketDetalle.fechaCierre ? calcularTiempoResolucion(ticketDetalle.fechaCreacion, ticketDetalle.fechaCierre) : 'En proceso' }}
                  </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Feed de Soluciones del Técnico -->
              <div v-if="solucionesVisibles.length > 0" class="solutions-feed-section">
                <div class="feed-header">
                  <h3>🛠️ Historial de Soluciones y Seguimientos</h3>
                  <span class="feed-count">{{ solucionesVisibles.length }} {{ solucionesVisibles.length === 1 ? 'registro' : 'registros' }}</span>
            </div>
                
                <div class="solutions-feed">
                  <div 
                    v-for="solucion in solucionesVisibles" 
                    :key="solucion.id" 
                    class="feed-item"
                    :class="`feed-type-${solucion.tipo}`"
                  >
                    <div class="feed-avatar">
                      <span class="avatar-icon">👨‍💻</span>
          </div>
                    <div class="feed-content">
                      <div class="feed-header">
                        <strong class="feed-author">{{ solucion.tecnico }}</strong>
                        <span class="feed-type-badge" :class="`badge-${solucion.tipo}`">
                          {{ getTipoSolucionText(solucion.tipo) }}
                        </span>
                        <span class="feed-time">{{ formatearFechaHora(solucion.fecha) }}</span>
        </div>
                        <div v-if="solucion.accionLabel" class="feed-action">
                          {{ solucion.accionLabel }}
                        </div>
                      <div class="feed-message">
                        {{ solucion.solucion }}
                      </div>
                      <div v-if="solucion.tiempoTexto" class="feed-extra-info">
                        ⏱️ {{ solucion.tiempoTexto }}
                      </div>
                      <div v-if="solucion.exitosa !== null && solucion.exitosa !== undefined" class="feed-extra-info">
                        ✅ {{ solucion.exitosa ? 'Marcada como exitosa' : 'Marcada como no exitosa' }}
                      </div>
                      
                      <!-- Mostrar imágenes si existen -->
                      <div v-if="solucion.imagenes && solucion.imagenes.length > 0" class="feed-images">
                        <div class="feed-images-grid">
                          <div v-for="(imagen, index) in solucion.imagenes" :key="index" class="feed-image-item">
                            <img :src="imagen.url" :alt="imagen.nombre" class="feed-image" @click="openImageModal(imagen)" />
                </div>
                  </div>
                  </div>
                </div>
                  </div>
                  </div>
                </div>
            </div>
          </div>
        </div>
              <!-- Formulario para Agregar Nueva Solución (solo para administradores y técnicos) -->
              <div v-if="esAdminOTecnico && (ticketDetalle.estado === 'en_proceso' || ticketDetalle.estado === 'resuelto')" class="new-solution-section">
                <div class="new-solution-header">
                  <h3>💬 Agregar Solución</h3>
                  <span class="solution-status">{{ getEstadoText(ticketDetalle.estado, esAdmin) }}</span>
                </div>
                
                <div class="new-solution-form">
                  <div class="form-group">
                    <label>Tipo de Solución</label>
                    <select v-model="nuevaSolucion.tipo" class="detail-input">
                      <option value="solucion_inicial" :disabled="ticketDetalle.estado === 'resuelto'">🛠️ Solución</option>
                      <option value="seguimiento" :disabled="ticketDetalle.estado !== 'resuelto'">📋 Seguimiento</option>
                    </select>
                  </div>
                  
                  <div class="form-group">
                    <label>Descripción de la Solución</label>
                  <textarea 
                      v-model="nuevaSolucion.solucion" 
                    class="detail-textarea"
                      placeholder="Describe los pasos que seguiste, actualizaciones, o cualquier información relevante..."
                      rows="4"
                  ></textarea>
                </div>
                
                  <div class="form-group">
                    <label>📷 Adjuntar Imágenes (Opcional)</label>
                    <div class="image-upload-section">
                    <input 
                      type="file" 
                        ref="solutionImageInput" 
                        @change="handleSolutionImageUpload" 
                      accept="image/*" 
                      multiple 
                        class="image-input"
                        id="solutionImageUpload"
                    />
                      <label for="solutionImageUpload" class="image-upload-btn">
                        📷 Seleccionar Imágenes
                    </label>
                      <p class="image-help-text">Puedes seleccionar múltiples imágenes para documentar la solución</p>
                  </div>
                  
                    <!-- Vista previa de imágenes seleccionadas -->
                    <div v-if="nuevaSolucion.imagenes && nuevaSolucion.imagenes.length > 0" class="images-preview">
                      <h4>📸 Imágenes Seleccionadas ({{ nuevaSolucion.imagenes.length }})</h4>
                      <div class="images-grid">
                        <div v-for="(imagen, index) in nuevaSolucion.imagenes" :key="index" class="image-item">
                          <img :src="imagen.url" :alt="imagen.nombre" class="image-preview" />
                          <div class="image-info">
                            <span class="image-name">{{ imagen.nombre }}</span>
                            <button @click="removeSolutionImage(index)" class="remove-image-btn">❌</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                  <div class="form-actions">
                    <button @click="agregarSolucion" class="add-solution-btn" :disabled="!nuevaSolucion.solucion.trim()">
                      💬 Agregar Solución
                  </button>
                    <button @click="limpiarFormularioSolucion" class="clear-btn">
                      🗑️ Limpiar
                    </button>
                  </div>
                </div>
              </div>
      </div>
      </div>

      <!-- Modal para ver imágenes en tamaño completo -->
      <div v-if="imagenModal.visible" class="image-modal-overlay" @click="closeImageModal">
        <div class="image-modal-content" @click.stop>
          <div class="image-modal-header">
            <h3>{{ imagenModal.imagen.nombre }}</h3>
            <button @click="closeImageModal" class="close-modal-btn">❌</button>
          </div>
          <div class="image-modal-body">
            <img :src="imagenModal.imagen.url" :alt="imagenModal.imagen.nombre" class="modal-image" />
              </div>
      </div>
      </div>



    <!-- Modal de formulario para nuevo ticket -->
    <div v-if="showFormModal" class="modal-overlay" @click.self="cerrarFormulario">
      <div class="modal-content form-modal">
        <div class="modal-header">
          <h3>📝 Crear Nuevo Ticket</h3>
          <button class="close-btn" @click="cerrarFormulario">✕</button>
        </div>
        <form class="ticket-form-modal" @submit.prevent="crearTicketCompleto" @submit="console.log('🔍 Formulario enviado')">
          <!-- Información Básica -->
          <div class="form-row">
            <div class="form-group">
              <label for="nombre">👤 Nombre del Solicitante</label>
              <input type="text" id="nombre" v-model="form.nombre" required readonly />
            </div>
            <div class="form-group">
              <label for="solicitudArea">Área Requerida <span class="required">*</span></label>
              <select id="solicitudArea" v-model="form.solicitudArea" required>
                <option value="">Selecciona un área</option>
                <option value="TECNOLOGIAS">💻 Tecnologías</option>
                <option value="MANTENIMIENTO">🔧 Mantenimiento</option>
                <option value="BIOMEDICA">🏥 Biomédica</option>
              </select>
            </div>
          </div>

          <!-- Título del Problema -->
          <div class="form-group">
            <label for="titulo">Título del Problema <span class="required">*</span></label>
            <input id="titulo" v-model="form.titulo" type="text" required placeholder="Ej: Falla en sistema de aire acondicionado" />
          </div>

          <!-- Descripción -->
          <div class="form-group">
            <label for="descripcion">Descripción Detallada <span class="required">*</span></label>
            <textarea id="descripcion" v-model="form.descripcion" rows="2" required placeholder="Describe el problema en detalle..."></textarea>
          </div>

          <!-- Ubicación -->
          <div class="form-row">
            <div class="form-group">
              <label for="areaSolicita"> Área que Solicita <span class="required">*</span></label>
              <div class="autocomplete-container">
                <v-autocomplete
                  id="areaSolicita"
                  v-model="form.areaSolicita"
                  :items="areasDisponibles"
                  item-text="nombre"
                  item-value="nombre"
                  :filter="filtrarAreas"
                  label="Selecciona o escribe el área"
                  placeholder="Ej: Quirófano, Enfermería"
                  :rules="[v => !!v || 'El área es requerida']"
                  required
                  clearable
                  dense
                  outlined
                  hide-details
                  class="area-autocomplete"
                >
                  <template v-slot:prepend-item>
                    <v-list-item @click="form.areaSolicita = ''">
                      <v-list-item-content>
                        <v-list-item-title>Limpiar selección</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider class="mt-2"></v-divider>
                  </template>
                </v-autocomplete>
              </div>
            </div>
            <div class="form-group">
              <label for="ubicacion"> Ubicación Específica <span class="required">*</span></label>
              <input id="ubicacion" v-model="form.ubicacion" type="text" required placeholder="Ej: Quirófano" />
            </div>
          </div>
          


          <!-- Equipo e Insumos -->
          <div class="form-row">
            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="form.tieneEquipo" />
                <span class="checkbox-custom"></span>
                ⚙️ ¿Hay equipo afectado?
              </label>
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="form.tieneInsumos" />
                <span class="checkbox-custom"></span>
                📦 ¿Necesita insumos?
              </label>
            </div>
          </div>
          
          <!-- Formulario de equipo que se muestra solo si se selecciona la casilla -->
          <div v-if="form.tieneEquipo" class="equipo-form">
            <div class="form-group">
              <label for="equipo">🔧 Equipo Afectado <span class="required">*</span></label>
              <input id="equipo" v-model="form.equipo" type="text" :required="form.tieneEquipo" placeholder="Ej: Sistema de aire acondicionado, Computadora, etc." />
            </div>
            <div class="form-group">
              <label for="serialNumber">🏷️ Número de Serie</label>
              <input id="serialNumber" v-model="form.serialNumber" type="text" placeholder="Número de serie (si aplica)" />
            </div>
          </div>

          <!-- Formulario de insumos que se muestra solo si se selecciona la casilla -->
          <div v-if="form.tieneInsumos" class="insumos-form">
            <div class="form-group">
              <label for="insumos">📦 Insumos Requeridos <span class="required">*</span></label>
              <div class="autocomplete-container">
                <input 
                  id="insumos" 
                  v-model="form.insumos" 
                  type="text" 
                  :required="form.tieneInsumos"
                  placeholder="Ej: Cartuchos de tinta, Papel, Consumibles médicos, etc." 
                  @input="buscarSugerenciasInsumos"
                  @focus="mostrarSugerencias = true"
                  @blur="ocultarSugerencias"
                />
                <div v-if="mostrarSugerencias && sugerenciasInsumos.length > 0" class="sugerencias-dropdown">
                  <div 
                    v-for="sugerencia in sugerenciasInsumos" 
                    :key="sugerencia.textoCompleto"
                    class="sugerencia-item"
                    @mousedown="seleccionarSugerencia(sugerencia)"
                  >
                    <div class="sugerencia-descripcion">{{ sugerencia.descripcion }}</div>
                    <div class="sugerencia-presentacion">{{ sugerencia.presentacion }}</div>
                  </div>
                </div>
              </div>
            </div>
            <div class="form-row">
            <div class="form-group">
              <label for="cantidadInsumos">🔢 Cantidad Requerida</label>
              <div class="quantity-counter">
                <button type="button" @click="decrementarCantidad" class="counter-btn">-</button>
                <input id="cantidadInsumos" v-model="form.cantidadInsumos" type="number" min="1" max="999" class="counter-input" />
                <button type="button" @click="incrementarCantidad" class="counter-btn">+</button>
              </div>
            </div>
            <div class="form-group">
              <label for="descripcionInsumos">Observaciones</label>
              <textarea id="descripcionInsumos" v-model="form.descripcionInsumos" rows="2" placeholder="Ej: 1 caja de 60 cubrebocas, 2 paquetes de papel A4, etc..."></textarea>
              </div>
            </div>
          </div>

          <!-- Notas -->
          <div class="form-group">
            <label for="notas">Notas y Comentarios</label>
            <textarea id="notas" v-model="form.notas" rows="1" placeholder="Información adicional útil para el técnico..."></textarea>
          </div>

          <!-- Botones de Acción -->
          <div class="modal-actions">
            <button type="button" @click="cerrarFormulario" class="cancel-btn">❌ Cancelar</button>
            <button type="submit" class="submit-btn" @click="probarBoton" :disabled="isCreatingTicket">
              <span v-if="!isCreatingTicket">✅ Crear Ticket</span>
              <span v-else>⏳ Creando...</span>
            </button>
          </div>
        </form>
      </div>
    </div>

   <!-- Componente de Estadísticas -->
    <TicketStatsModal
      :show-tickets-creados-resueltos="showTicketsCreadosResueltos"
      :show-tiempo-promedio="showTiempoPromedio"
      :show-tickets-por-tecnico="showTicketsPorTecnico"
      :show-tickets-por-area="showTicketsPorArea"
      :show-problemas-mas-comunes="showProblemasMasComunes"
      :tickets="tickets"
      @cerrar-tickets-creados-resueltos="cerrarTicketsCreadosResueltos"
      @cerrar-tiempo-promedio="cerrarTiempoPromedio"
      @cerrar-tickets-por-tecnico="cerrarTicketsPorTecnico"
      @cerrar-tickets-por-area="cerrarTicketsPorArea"
      @cerrar-problemas-mas-comunes="cerrarProblemasMasComunes"
    />

    <!-- Modal de Reasignación -->
    <div v-if="showReassignModal" class="modal-overlay" @click="cancelarReasignacion">
      <div class="modal-content reassign-modal" @click.stop>
        <div class="modal-header">
          <h3>🔄 Reasignar Ticket #{{ ticketToReassign?.id }}</h3>
          <button @click="cancelarReasignacion" class="close-btn">&times;</button>
        </div>
        
        <div class="modal-body">
          <div class="reassign-info">
            <p><strong>Ticket:</strong> {{ ticketToReassign?.titulo }}</p>
            <p><strong>Área actual:</strong> {{ getSolicitudText(ticketToReassign?.solicitudArea) }}</p>
          </div>
          
          <div class="form-group">
            <label for="razonReasignacion">📝 Razón de la reasignación <span class="required">*</span></label>
            <textarea 
              id="razonReasignacion"
              v-model="reassignData.razon" 
              placeholder="Explica por qué necesitas reasignar este ticket..."
              rows="3"
              required
            ></textarea>
          </div>
          
          <div class="form-group">
            <label for="nuevaAreaReasignacion">🎯 Área de destino <span class="required">*</span></label>
            <select id="nuevaAreaReasignacion" v-model="reassignData.nuevaArea" required>
              <option value="">Selecciona el área de destino</option>
              <option value="TECNOLOGIAS">Tecnologías</option>
              <option value="MANTENIMIENTO">Mantenimiento</option>
              <option value="BIOMEDICA">Biomédica</option>
            </select>
          </div>
        </div>
        
        <div class="modal-footer">
          <button @click="cancelarReasignacion" class="btn btn-secondary">Cancelar</button>
          <button @click="confirmarReasignacion" class="btn btn-primary">Confirmar Reasignación</button>
        </div>
      </div>
    </div>

    <!-- Modal de Cancelación -->
    <div v-if="showCancelModal" class="modal-overlay" @click="cancelarCancelacion">
      <div class="modal-content cancel-modal" @click.stop>
        <div class="modal-header">
          <h3>❌ Cancelar Ticket {{ generarFolio(ticketToCancel) }}</h3>
          <button @click="cancelarCancelacion" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <div class="cancel-info">
            <p><strong>Ticket:</strong> {{ ticketToCancel?.titulo }}</p>
            <p><strong>Estado actual:</strong> {{ getEstadoText(ticketToCancel?.estado, esAdmin) }}</p>
            <p><strong>Área:</strong> {{ getSolicitudText(ticketToCancel?.solicitudArea) }}</p>
          </div>
          
          <div class="form-group">
            <label for="motivoCancelacion">📝 Motivo de la cancelación <span class="required">*</span></label>
            <textarea 
              id="motivoCancelacion"
              v-model="cancelData.motivo" 
              placeholder="Explica por qué necesitas cancelar este ticket..."
              rows="4"
              required
            ></textarea>
          </div>
          
          <div class="warning-message">
            <p>⚠️ <strong>Advertencia:</strong> Una vez cancelado, este ticket no podrá ser reabierto. Asegúrate de que la cancelación sea necesaria.</p>
          </div>
        </div>
        
        <div class="modal-footer">
          <button @click="cancelarCancelacion" class="btn btn-secondary">No Cancelar</button>
          <button @click="confirmarCancelacion" class="btn btn-danger">Confirmar Cancelación</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import InsumosService from './insumos-service.js';
// ✅ InsumosService se conecta al BACKEND (InsumosController.groovy)
import TicketStatsModal from './TicketStatsModal.vue';


export default {
  name: 'TicketForm',
  props: {
    role: {
      type: String,
      default: 'personal',
    },
    userName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isCreatingTicket: false, // Estado de carga para el botón crear ticket
      form: {
        nombre: '',
        titulo: '',
        descripcion: '',
        areaSolicita: '',
        ubicacion: '',
        equipo: '',
        tieneEquipo: false,
        descripcionEquipo: '',
        tieneInsumos: false,
        insumos: '',
        descripcionInsumos: '',
        cantidadInsumos: 1,
        solicitudArea: '',
        serialNumber: '',
        notas: '',
        fechaCreacion: '',
        fechaCierre: '',
      },
      tickets: [],
      nextId: 1,
      ticketDetalle: null,
      showTicketDetail: false,
      mostrarAccionesEstadisticas: false,
      showFormModal: false,
      showTicketsCreadosResueltos: false,
      showTiempoPromedio: false,
      showTicketsPorTecnico: false,
      showTicketsPorArea: false,
      showProblemasMasComunes: false,
      filtroArea: '',
      filtroPrioridad: '',
      filtroEstado: 'activos', // 'activos', 'resueltos', 'cancelados', 'todos'
      areasDisponibles: [], // Lista de áreas desde catalogo_areas
      // Formulario para nueva solución
            nuevaSolucion: {
              solucion: '',
              tipo: 'solucion_inicial',
              imagenes: []
            },
            // Modal para ver imágenes
            imagenModal: {
              visible: false,
              imagen: { nombre: '', url: '' }
            },
      listaTecnicos: [], // Se cargarán desde el backend (array de objetos {id, nombre})
      listaTecnicosNombres: [], // Solo nombres para compatibilidad
      // Propiedades para autocompletado de insumos
      sugerenciasInsumos: [],
      mostrarSugerencias: false,
      // Variable para guardar estados anteriores de solicitud
      solicitudAreaAnterior: {},
      // Variables para manejo de fotos adjuntas
      fotosSeleccionadas: [],
      // Variables para modal de reasignación
      showReassignModal: false,
      ticketToReassign: null,
      reassignData: {
        razon: '',
        nuevaArea: ''
      },
      // Variables para modal de cancelación
      showCancelModal: false,
      ticketToCancel: null,
      cancelData: {
        motivo: ''
      },
      historialSoluciones: [],
    };
  },
  computed: {
    esAdmin() {
      // Verificar si el usuario es administrador (acceso total)
      const roles = this.$store.state.security.roles || [];
      const rolesArray = Array.isArray(roles) ? roles : (typeof roles === 'string' ? roles.split(',') : []);
      return rolesArray.some(r => r.trim() === 'ROLE_ADMIN');
    },
    
    esTecnico() {
      // Verificar si el usuario es técnico (cualquier tipo)
      const roles = this.$store.state.security.roles || [];
      const rolesArray = Array.isArray(roles) ? roles : (typeof roles === 'string' ? roles.split(',') : []);
      return rolesArray.some(r => 
        ['ROLE_TECNICO_MTTO', 'ROLE_TECNICO_TEC', 'ROLE_TECNICO_BIO'].includes(r.trim())
      );
    },
    
    esAdminOTecnico() {
      // Verificar si tiene permisos de administración (admin o técnico)
      return this.esAdmin || this.esTecnico;
    },
    
    areaTecnico() {
      // Determinar el área del técnico según su rol
      const roles = this.$store.state.security.roles || [];
      const rolesArray = Array.isArray(roles) ? roles : (typeof roles === 'string' ? roles.split(',') : []);
      
      if (rolesArray.some(r => r.trim() === 'ROLE_TECNICO_MTTO')) {
        return 'MANTENIMIENTO';
      }
      if (rolesArray.some(r => r.trim() === 'ROLE_TECNICO_TEC')) {
        return 'TECNOLOGIAS';
      }
      if (rolesArray.some(r => r.trim() === 'ROLE_TECNICO_BIO')) {
        return 'BIOMEDICA';
      }
      return null;
    },
    solucionesVisibles() {
      if (this.historialSoluciones && this.historialSoluciones.length > 0) {
        return this.historialSoluciones;
      }
      if (!this.ticketDetalle || !this.ticketDetalle.solucionesTecnico) {
        return [];
      }
      return this.ticketDetalle.solucionesTecnico;
    },
    ticketsFiltrados() {
      let tickets = this.tickets;

      // 1. FILTRO POR ROL - Determinar qué tickets puede ver el usuario
      if (this.esAdmin) {
        // Admin ve TODOS los tickets
        tickets = this.tickets;
      } else if (this.esTecnico && this.areaTecnico) {
        // Técnico ve solo tickets de su área
        tickets = this.tickets.filter(t =>
          t.solicitudArea?.toUpperCase() === this.areaTecnico
        );
      } else {
        // Usuario normal (ROLE_PACIENTES) ve todos los tickets de su hospital
        // Luego aplicamos la regla de 24h y el filtro por estado más abajo
        tickets = this.tickets;
      }

      // 2. Filtrar por área solicitante (filtro manual del usuario)
      if (this.filtroArea) {
        const filtroNormalizado = this.normalizarTexto(this.filtroArea);
        tickets = tickets.filter(t => this.normalizarTexto(t.areaSolicita) === filtroNormalizado);
      }

      // 3. Filtrar por prioridad (solo para administradores y técnicos)
      if (this.esAdminOTecnico && this.filtroPrioridad) {
        tickets = tickets.filter(t => t.prioridad === this.filtroPrioridad);
      }

      // 4. Regla de 24 horas para tickets resueltos de usuarios normales
      const MILISEGUNDOS_EN_DIA = 24 * 60 * 60 * 1000;

      const esTicketResueltoReciente = (ticket) => {
        // Si el ticket no está resuelto, no aplica la regla
        if (!ticket.esResuelto) return true;
        
        // Para tickets resueltos, verificar la fecha de cierre
        const fechaCierre = this.obtenerFechaFinalizacion(ticket);
        if (!fechaCierre) return false;
        
        const fechaMs = new Date(fechaCierre).getTime();
        if (Number.isNaN(fechaMs)) return false;
        
        const diferencia = Date.now() - fechaMs;
        // Si la fecha es futura, mostrarlo de todos modos
        if (diferencia < 0) return true;
        
        // Solo mostrar si tiene menos de 24 horas de resuelto
        return diferencia <= MILISEGUNDOS_EN_DIA;
      };

      // Aplicar filtro de estado
      if (this.esAdminOTecnico) {
        switch (this.filtroEstado) {
          case 'resueltos':
            // Mostrar TODOS los tickets resueltos sin importar el tiempo
            tickets = tickets.filter(t => t.esResuelto);
            break;
          case 'cancelados':
            tickets = tickets.filter(t => t.esCancelado);
            break;
          case 'todos':
            // No aplicar filtro de estado
            break;
          case 'activos':
          default:
            // Mostrar solo tickets no finalizados
            tickets = tickets.filter(t => !this.esTicketFinalizado(t));
            break;
        }
      } else {
        // Para usuarios normales:
        // - Mostrar tickets no finalizados
        // - Mostrar sus propios tickets resueltos en las últimas 24 horas
        tickets = tickets.filter(t => {
          if (!this.esTicketFinalizado(t)) return true;
          
          // Si es un ticket resuelto, verificar si es del usuario y reciente
          if (t.esResuelto) {
            const usuarioId = this.$store.state.security.user?.id;
            if (!usuarioId) return false;

            // Determinar si el ticket pertenece al usuario actual.
            const perteneceAlUsuario = (
              (t.usuarioId && t.usuarioId === usuarioId) ||
              (t.solicitanteId && t.solicitanteId === usuarioId) ||
              // Fallback: comparar por nombre de usuario si no hay ids
              (t.nombre && this.$store.state.security.username && t.nombre === this.$store.state.security.username)
            );

            return perteneceAlUsuario && esTicketResueltoReciente(t);
          }
          
          return false;
        });
      }
    

      // 5. Ordenar tickets activos por prioridad dejando finales al final
      tickets.sort((a, b) => {
        const esFinalizadoA = this.esTicketFinalizado(a);
        const esFinalizadoB = this.esTicketFinalizado(b);
        if (esFinalizadoA !== esFinalizadoB) {
          return esFinalizadoA ? 1 : -1; // Finalizados siempre al final
        }

        const prioridadA = this.obtenerNivelPrioridad(a.prioridad);
        const prioridadB = this.obtenerNivelPrioridad(b.prioridad);

        if (prioridadA !== prioridadB) {
          return prioridadB - prioridadA;
        }

        const fechaA = new Date(a.fechaCreacion);
        const fechaB = new Date(b.fechaCreacion);
        return fechaB - fechaA; // Más recientes primero
      });

      return tickets;
    },
  },
  mounted() {
    // Establecer el nombre del usuario logueado desde el store
    if (this.userName) {
      this.form.nombre = this.userName;
    } else if (this.$store.state.security.username) {
      this.form.nombre = this.$store.state.security.username;
    }
    
    // Cargar datos desde el backend
    this.cargarTicketsDesdeBackend();
    this.cargarTecnicosDesdeBackend();
    this.cargarAreasDesdeBackend();
  },
  components: {
    TicketStatsModal
  },
  methods: {
    // Los roles se obtienen automáticamente del store de Vuex (this.$store.state.security.roles)
    // No se necesita método para cambiar roles manualmente
    normalizarTexto(valor) {
      if (valor === null || valor === undefined) return '';
      try {
        return valor
          .toString()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toUpperCase()
          .trim();
      } catch (error) {
        return valor.toString().toUpperCase().trim();
      }
    },
    filtrarAreas(item, queryText, itemText) {
      const textoBuscar = this.normalizarTexto(queryText);
      if (!textoBuscar) return true;
      const textoItem = this.normalizarTexto(itemText || item?.nombre || '');
      return textoItem.includes(textoBuscar);
    },
    toggleAccionesEstadisticas() {
      this.mostrarAccionesEstadisticas = !this.mostrarAccionesEstadisticas;
    },
    verTicketsCreadosResueltos() {
      this.cerrarDashboards();
      this.showTicketsCreadosResueltos = true;
      this.mostrarAccionesEstadisticas = false;
    },
    verTiempoPromedio() {
      this.cerrarDashboards();
      this.showTiempoPromedio = true;
      this.mostrarAccionesEstadisticas = false;
    },
    verTicketsPorTecnico() {
      this.cerrarDashboards();
      this.showTicketsPorTecnico = true;
      this.mostrarAccionesEstadisticas = false;
    },
    verTicketsPorArea() {
      this.cerrarDashboards();
      this.showTicketsPorArea = true;
      this.mostrarAccionesEstadisticas = false;
    },
	verProblemasMasComunes() {
      this.cerrarDashboards();
      this.showProblemasMasComunes = true;
      this.mostrarAccionesEstadisticas = false;
    },
    cerrarDashboards() {
      this.showTicketsCreadosResueltos = false;
      this.showTiempoPromedio = false;
      this.showTicketsPorTecnico = false;
      this.showTicketsPorArea = false;
    },
    
    
    
    
    // Cargar tickets desde el backend
    async cargarTicketsDesdeBackend() {
      try {
        const response = await this.$http({
          url: '/mantenimiento/tickets/listarTickets',
          method: 'GET',
          params: {
            area: this.filtroArea || undefined,
            estado: this.filtroPrioridad ? this.filtroPrioridad.toUpperCase() : undefined,
          }
        });
        
        if (response.data && response.data.root) {
          // Mapear los datos del backend al formato del frontend
          this.tickets = response.data.root.map(ticket => ({
            id: ticket.id,
            folio: ticket.folio || ticket.id,
            nombre: ticket.nombre,
            titulo: ticket.titulo,
            descripcion: ticket.descripcion,
            areaSolicita: ticket.areaSolicita,
            ubicacion: ticket.ubicacion,
            equipo: ticket.equipo,
            solicitudArea: ticket.solicitudArea?.toLowerCase(),
            serialNumber: ticket.serialNumber,
            notas: ticket.notas,
            insumos: ticket.insumos,
            cantidadInsumos: ticket.cantidadInsumos,
            descripcionInsumos: ticket.descripcionInsumos,
            prioridad: ticket.prioridad || 'MEDIA',  // Backend retorna: ALTA, MEDIA, BAJA
            prioridadColor: this.obtenerColorPrioridad(ticket.prioridad),
            estado: ticket.estado || 'PENDIENTE',  // Backend retorna: PENDIENTE, ASIGNADO, EN_PROCESO, RESUELTO, CANCELADO
            estadoAnterior: ticket.estado || 'PENDIENTE',  // Guardar estado original para restaurar si cancela reasignación
            tecnico: ticket.tecnicoAsignado,
            tecnicoAsignadoId: ticket.tecnicoAsignadoId,
            comentariosInternos: ticket.comentariosInternos,
            fechaCreacion: ticket.fechaCreacion,
            fechaCierre: ticket.fechaCierre,
            fechaAsignacion: ticket.fechaAsignacion,
            solucionesTecnico: ticket.solucionesTecnico || [],
            // Flags de permisos del backend
            esCancelado: ticket.esCancelado || false,
            esResuelto: ticket.esResuelto || false,
            puedeEditar: ticket.puedeEditar !== false,
            puedeCancelar: ticket.puedeCancelar || false,
          }));
          
          console.log('✅ Tickets cargados desde backend:', this.tickets.length);
        }
      } catch (error) {
        console.error('❌ Error al cargar tickets desde backend:', error);
        this.$store.dispatch('security/revisaError', error);
      }
    },
    
    async cargarTecnicosDesdeBackend() {
      try {
        const response = await this.$http({
          url: '/mantenimiento/tickets/listarTecnicos',
          method: 'GET',
        });
        
        if (response.data && response.data.root) {
          // El backend devuelve un array de objetos con { id, nombre, area, etc. }
          if (Array.isArray(response.data.root)) {
            // Guardar lista completa con IDs
            this.listaTecnicos = response.data.root.map(t => ({
              id: t.id,
              nombre: typeof t === 'string' ? t : (t.nombre || t.nombreCompleto || `${t.nombres} ${t.apellidoPaterno || ''}`.trim())
            }));
            // Lista de solo nombres para compatibilidad
            this.listaTecnicosNombres = this.listaTecnicos.map(t => t.nombre);
            console.log(`✅ ${this.listaTecnicos.length} técnicos cargados desde el backend`);
          }
        }
      } catch (error) {
        console.error('Error al cargar técnicos desde backend:', error);
        // No mostrar notificación al usuario, usar lista vacía
        this.listaTecnicos = [];
        console.warn('⚠️ No se pudieron cargar técnicos. Usando lista vacía.');
      }
    },
    
    // Cargar áreas desde catalogo_areas
    async cargarAreasDesdeBackend() {
      try {
        const response = await this.$http({
          url: '/mantenimiento/tickets/listarAreas',
          method: 'GET',
        });
        
        if (response.data && response.data.success && response.data.root) {
          this.areasDisponibles = response.data.root.map(area => {
            const nombre = area.nombre || '';
            return {
              id: area.id,
              nombre,
              activo: area.activo,
              nombreNormalizado: this.normalizarTexto(nombre)
            };
          });
          console.log(`✅ ${this.areasDisponibles.length} áreas cargadas desde catalogo_areas`);
        }
      } catch (error) {
        console.error('Error al cargar áreas desde backend:', error);
        // Usar lista vacía si falla
        this.areasDisponibles = [];
        console.warn('⚠️ No se pudieron cargar áreas. Usando lista vacía.');
      }
    },
    
    capitalizarPrioridad(prioridad) {
      if (!prioridad) return 'Media';
      const pri = prioridad.toLowerCase();
      return pri.charAt(0).toUpperCase() + pri.slice(1);
    },
    
    obtenerColorPrioridad(prioridad) {
      if (!prioridad) return '#FFCC00';
      const pri = prioridad.toUpperCase();
      const colores = {
        'BAJA': '#00CC00',
        'MEDIA': '#FFCC00',
        'ALTA': '#FF6600',
      };
      return colores[pri] || '#FFCC00';
    },
    
    // Método eliminado - ahora se cargan tickets desde el backend
    
    probarBoton() {
      console.log('🔍 Botón Crear Ticket clickeado');
      console.log('🔍 Estado del formulario:', this.form);
      
      // Prevenir múltiples clics
      if (this.isCreatingTicket) {
        console.log('⏳ Ya se está creando un ticket, ignorando clic adicional');
        return;
      }
      
      try {
      // Llamar al método principal
      this.crearTicketCompleto();
      } catch (error) {
        console.error('❌ Error en probarBoton:', error);
        this.mostrarNotificacion(`❌ Error al procesar el formulario: ${error.message || 'Error desconocido'}`);
        this.isCreatingTicket = false; // Resetear estado en caso de error
      }
    },
    async crearTicketCompleto(event) {
      console.log('🚀 TicketForm: Método crearTicketCompleto ejecutado');
      console.log('🔍 Evento recibido:', event);
      
      // Activar estado de carga
      this.isCreatingTicket = true;
      
      try {
        // Prevenir el comportamiento por defecto del formulario
        if (event) {
          event.preventDefault();
        }
        
        // Validar campos requeridos antes de crear el ticket
        console.log('🔍 TicketForm: Validando formulario...');
        console.log('🔍 Formulario actual:', this.form);
        
        // Verificar campos requeridos
        const camposRequeridos = ['nombre', 'titulo', 'descripcion', 'areaSolicita', 'ubicacion', 'solicitudArea'];
        const camposFaltantes = camposRequeridos.filter(campo => !this.form[campo] || this.form[campo].trim() === '');
        
        if (camposFaltantes.length > 0) {
          console.error('❌ TicketForm: Campos requeridos faltantes:', camposFaltantes);
          this.mostrarNotificacion(`❌ Por favor completa los campos requeridos: ${camposFaltantes.join(', ')}`);
          this.isCreatingTicket = false;
          return;
        }
        
        // Preparar datos para enviar al backend
        // El BACKEND calculará la prioridad automáticamente
        const datosTicket = {
          nombre: this.form.nombre,
          titulo: this.form.titulo,
          descripcion: this.form.descripcion,
          areaSolicita: this.form.areaSolicita,
          ubicacion: this.form.ubicacion,
          solicitudArea: this.form.solicitudArea.toUpperCase(), // MANTENIMIENTO, TECNOLOGIAS, BIOMEDICA
          // NO enviamos prioridad - el backend la calculará
          equipo: this.form.equipo || null,
          serialNumber: this.form.serialNumber || null,
          descripcionEquipo: this.form.descripcionEquipo || null,
          insumos: this.form.insumos || null,
          cantidadInsumos: this.form.cantidadInsumos || null,
          descripcionInsumos: this.form.descripcionInsumos || null,
          notas: this.form.notas || null,
        };
        
        console.log(' Enviando datos al backend:', datosTicket);
        
        // Enviar al backend (backend calculará la prioridad)
        const response = await this.$http({
          url: '/mantenimiento/tickets/crearTicket',
          method: 'POST',
          data: datosTicket,
        });
        
        console.log('📥 Respuesta del backend:', response.data);
        
        if (response.data.success || response.data.estado) {
          // El backend devuelve el ticket con la prioridad ya calculada
          const ticketCreado = response.data.root;
          const prioridadAsignada = ticketCreado.prioridad || 'MEDIA';
          
          // Mostrar notificación de éxito con la prioridad asignada por el backend
          this.mostrarNotificacion(`✅ Ticket creado exitosamente con prioridad ${this.capitalizarPrioridad(prioridadAsignada)}`);
          
          // Recargar lista de tickets desde el backend
          await this.cargarTicketsDesdeBackend();
          
          // Cerrar modal y limpiar formulario
          this.cerrarFormulario();
        } else {
          throw new Error(response.data.mensaje || 'Error al crear el ticket');
        }
        
      } catch (error) {
        console.error('❌ TicketForm: Error general en crearTicketCompleto:', error);
        this.mostrarNotificacion(`❌ Error al crear el ticket: ${error.response?.data?.mensaje || error.message || 'Error desconocido'}`);
        this.$store.dispatch('security/revisaError', error);
        // No cerrar el modal si hay error para que el usuario pueda intentar de nuevo
      } finally {
        // Resetear estado de carga
        this.isCreatingTicket = false;
      }
    },

    guardarEstadoActual(ticket) {
      // Guardar el estado actual antes de que el usuario lo cambie en el dropdown
      if (ticket && ticket.estado !== 'REASIGNAR') {
        ticket.estadoAnterior = (ticket.estado || '').toString().toUpperCase();
      }
    },

    async actualizarEstado(ticket) {
      console.log(`Estado actualizado para ticket ${ticket.id}: ${ticket.estado}`);
      
      const estadoDeseado = (ticket.estado || '').toString().toUpperCase();
      const tieneTecnico = this.tieneTecnicoAsignado(ticket);
      
      // Si el estado es "reasignar", abrir modal de reasignación
      if (estadoDeseado === 'REASIGNAR') {
        this.ticketToReassign = ticket;
        this.reassignData.razon = '';
        this.reassignData.nuevaArea = '';
        this.showReassignModal = true;
        return;
      }

      if (estadoDeseado === 'EN_PROCESO' && !tieneTecnico) {
        this.mostrarNotificacion('❌ Debes asignar un técnico antes de mover el ticket a "En Proceso".');
        this.revertirEstadoTicket(ticket);
        return;
      }

      if (estadoDeseado === 'RESUELTO' && !tieneTecnico) {
        this.mostrarNotificacion('❌ No puedes marcar el ticket como "Resuelto" sin un técnico asignado.');
        this.revertirEstadoTicket(ticket);
        return;
      }
      
      try {
        // Convertir estado a mayúsculas según backend
        const estadoBackend = estadoDeseado;
        const estadosValidos = {
          'PENDIENTE': 'PENDIENTE',
          'EN_PROCESO': 'EN_PROCESO',
          'RESUELTO': 'RESUELTO',
          'CANCELADO': 'CANCELADO'
        };
        
        const response = await this.$http({
          url: '/mantenimiento/tickets/actualizarEstado',
          method: 'POST',
          data: {
            ticketId: ticket.id,
            estado: estadosValidos[estadoBackend] || 'PENDIENTE',
            comentario: ticket.comentariosInternos || '',
          }
        });
        
        if (response.data.success || response.data.estado) {
          this.mostrarNotificacion(`✅ Estado actualizado correctamente`);
          // Recargar tickets desde backend
          await this.cargarTicketsDesdeBackend();
          if (this.ticketDetalle && this.ticketDetalle.id === ticket.id) {
            await this.cargarDetalleTicket(ticket.id);
          }
        }
      } catch (error) {
        console.error('❌ Error al actualizar estado:', error);
        this.mostrarNotificacion(`❌ Error al actualizar el estado`);
        this.$store.dispatch('security/revisaError', error);
        // Recargar para revertir cambio local
        await this.cargarTicketsDesdeBackend();
      }
    },
    tieneTecnicoAsignado(ticket) {
      if (!ticket) {
        return false;
      }
      const idDirecto = ticket.tecnicoAsignadoId;
      if (idDirecto !== null && idDirecto !== undefined && idDirecto !== '') {
        return true;
      }
      const idAlterno = ticket.tecnicoId || ticket.tecnicoAsignadoIdOriginal;
      if (idAlterno !== null && idAlterno !== undefined && idAlterno !== '') {
        return true;
      }
      if (ticket.tecnicoAsignado && ticket.tecnicoAsignado.id) {
        return true;
      }
      return false;
    },
    revertirEstadoTicket(ticket) {
      const estadoPrevio = (ticket.estadoAnterior || 'PENDIENTE').toString().toUpperCase();
      this.$nextTick(() => {
        ticket.estado = estadoPrevio;
        ticket.estadoAnterior = estadoPrevio;
      });
    },
    async establecerEstadoEnProcesoAutomatico(ticket) {
      if (!ticket || !ticket.id) {
        return;
      }
      const estadoActual = (ticket.estado || '').toString().toUpperCase();
      if (estadoActual === 'EN_PROCESO' || estadoActual === 'RESUELTO' || estadoActual === 'CANCELADO') {
        return;
      }
      try {
        await this.$http({
          url: '/mantenimiento/tickets/actualizarEstado',
          method: 'POST',
          data: {
            ticketId: ticket.id,
            estado: 'EN_PROCESO',
            comentario: ticket.comentariosInternos || ''
          }
        });
        ticket.estado = 'EN_PROCESO';
        ticket.estadoAnterior = 'EN_PROCESO';
        console.log(`🔧 Ticket ${ticket.id} marcado automáticamente como EN_PROCESO tras asignar técnico.`);
      } catch (error) {
        console.warn('No fue posible actualizar el ticket a EN_PROCESO automáticamente:', error);
      }
    },
    async actualizarTecnico(ticket) {
      const tecnicoId = ticket.tecnicoAsignadoId;
      console.log(`🔧 Asignando técnico ID ${tecnicoId} al ticket ${ticket.id}`);
      
      try {
        const response = await this.$http({
          url: '/mantenimiento/tickets/actualizarTecnico',
          method: 'POST',
          data: {
            ticketId: ticket.id,
            tecnicoId: tecnicoId || null,
          }
        });
        
        if (response.data.success || response.data.estado) {
          if (tecnicoId) {
            await this.establecerEstadoEnProcesoAutomatico(ticket);
          }
          const tecnicoNombre = tecnicoId ? 
            this.listaTecnicos.find(t => t.id === tecnicoId)?.nombre || 'Técnico' : 
            'Sin asignar';
          this.mostrarNotificacion(`✅ Técnico ${tecnicoNombre} asignado correctamente`);
          // Recargar tickets desde backend para actualizar todas las vistas
          await this.cargarTicketsDesdeBackend();
          if (this.ticketDetalle && this.ticketDetalle.id === ticket.id) {
            await this.cargarDetalleTicket(ticket.id);
          }
        }
      } catch (error) {
        console.error('❌ Error al actualizar técnico:', error);
        this.mostrarNotificacion(`❌ Error al asignar el técnico`);
        this.$store.dispatch('security/revisaError', error);
        // Recargar para revertir cambio local
        await this.cargarTicketsDesdeBackend();
      }
    },
    
    // Nueva función para asignar ticket usando el endpoint específico
    async asignarTicket(ticketId, tecnicoId, comentario = '') {
      console.log(`Asignando ticket ${ticketId} al técnico ${tecnicoId}`);
      
      try {
        const response = await this.$http({
          url: '/mantenimiento/tickets/asignarTicket',
          method: 'POST',
          data: {
            ticketId: ticketId,
            tecnicoId: tecnicoId,
            comentario: comentario
          }
        });
        
        if (response.data.success || response.data.estado) {
          this.mostrarNotificacion(`✅ Ticket asignado correctamente`);
          // Recargar tickets desde backend
          await this.cargarTicketsDesdeBackend();
          return true;
        }
        return false;
      } catch (error) {
        console.error('❌ Error al asignar ticket:', error);
        this.mostrarNotificacion(`❌ Error al asignar el ticket`);
        this.$store.dispatch('security/revisaError', error);
        return false;
      }
    },
    
    // función para obtener estadísticas generales
    async obtenerEstadisticas(filtros = {}) {
      console.log('Obteniendo estadísticas de tickets:', filtros);
      
      try {
        const response = await this.$http({
          url: '/mantenimiento/tickets/estadisticas',
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
          params: {
            fechaInicio: filtros.fechaInicio || undefined,
            fechaFin: filtros.fechaFin || undefined,
            area: filtros.area || undefined,
            tecnico: filtros.tecnico || undefined
          }
        });
        
        if (response.data && response.data.success) {
          return response.data.estadisticas || response.data;
        }
        return null;
      } catch (error) {
        console.error('❌ Error al obtener estadísticas:', error);
        this.$store.dispatch('security/revisaError', error);
        return null;
      }
    },
    
    // función para obtener historial de un ticket
    async obtenerHistorialTicket(ticketId) {
      console.log(`Obteniendo historial del ticket ${ticketId}`);
      
      try {
        const response = await this.$http({
          url: `/mantenimiento/tickets/obtenerHistorial/${ticketId}`,
          method: 'GET'
        });
        
        if (response.data && response.data.success) {
          return response.data.historial || response.data.root || [];
        }
        return [];
      } catch (error) {
        console.error('❌ Error al obtener historial:', error);
        this.$store.dispatch('security/revisaError', error);
        return [];
      }
    },
    
    // función para obtener estadísticas de un técnico específico
    async obtenerEstadisticasTecnico(tecnicoId, filtros = {}) {
      console.log(`Obteniendo estadísticas del técnico ${tecnicoId}:`, filtros);
      
      try {
        const response = await this.$http({
          url: `/mantenimiento/tickets/estadisticasTecnico/${tecnicoId}`,
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
          params: {
            fechaInicio: filtros.fechaInicio || undefined,
            fechaFin: filtros.fechaFin || undefined,
            periodo: filtros.periodo || undefined
          }
        });
        
        if (response.data && response.data.success) {
          return response.data.estadisticas || response.data;
        }
        return null;
      } catch (error) {
        console.error('❌ Error al obtener estadísticas del técnico:', error);
        this.$store.dispatch('security/revisaError', error);
        return null;
      }
    },
    
    actualizarSolicitudArea(ticket) {
      console.log(`Área de solicitud actualizada para ticket ${ticket.id}: ${ticket.solicitudArea}`);
      
      // VALIDACIÓN: Verificar si se puede cambiar el área de solicitud
      if (this.requiereValidacionSolucion(ticket)) {
        // Caso especial: ticket resuelto - BLOQUEAR completamente
        if (ticket.estado === 'resuelto') {
          alert(
            `🚫 CAMBIO NO PERMITIDO\n\n` +
            `El ticket #${ticket.id} está en estado "${this.getEstadoText(ticket.estado)}" y ya no se pueden realizar cambios.\n\n` +
            `Los tickets resueltos son de solo lectura para mantener la integridad de los datos.`
          );
          
          // Revertir el cambio inmediatamente
          this.$nextTick(() => {
            this.revertirSolicitudArea(ticket);
          });
          return;
        }
      }
      
      // La prioridad ya NO se recalcula en el frontend
      // El backend es responsable de calcular la prioridad cuando se crea o actualiza el ticket
      this.mostrarNotificacion(`🔄 Área de solicitud actualizada a: ${this.getSolicitudText(ticket.solicitudArea)}`);
    },
    requiereValidacionSolucion(ticket) {
      // Solo bloquear cambios en tickets resueltos
      // Con la nueva funcionalidad de reasignación, ya no se requiere validación de solución del técnico
      return ticket.estado === 'resuelto';
    },
    guardarEstadoAnterior(ticket) {
      // Guardar el valor actual antes de que el usuario lo cambie
      this.solicitudAreaAnterior[ticket.id] = ticket.solicitudArea;
      console.log(`Estado anterior guardado para ticket ${ticket.id}: ${ticket.solicitudArea}`);
    },
    revertirSolicitudArea(ticket) {
      // Restaurar el valor anterior
      const valorAnterior = this.solicitudAreaAnterior[ticket.id];
      if (valorAnterior !== undefined) {
        ticket.solicitudArea = valorAnterior;
        console.log(`Revertido área de solicitud para ticket ${ticket.id} a "${valorAnterior}"`);
      } else {
        // Fallback: usar el primer valor disponible
        ticket.solicitudArea = 'tecnologia';
      }
    },
    obtenerNivelPrioridad(prioridad) {
      // Convierte las prioridades en números para poder compararlas
      // Solo 3 niveles: Alta = 3, Media = 2, Baja = 1
      switch (prioridad?.toLowerCase()) {
        case 'alta':
          return 3;
        case 'media':
          return 2;
        case 'baja':
          return 1;
        default:
          return 2; // Por defecto, prioridad media
      }
    },
    esTicketFinalizado(ticket) {
      const estado = (ticket?.estado || '').toString().toUpperCase();
      return estado === 'RESUELTO' || estado === 'CANCELADO';
    },
    obtenerFechaFinalizacion(ticket) {
      return ticket?.fechaCierre || ticket?.fechaActualizacion || ticket?.fechaCreacion;
    },
    
    // Método para cambiar la prioridad manualmente (solo administradores y técnicos)
    async cambiarPrioridadManual(ticket) {
      if (!this.esAdminOTecnico || !ticket) return;
      
      const nuevaPrioridad = ticket.prioridad;
      const prioridadAnterior = ticket.prioridad || 'Media';
      
      try {
        const response = await this.$http({
          url: '/mantenimiento/tickets/actualizarPrioridad',
          method: 'POST',
          data: {
            ticketId: ticket.id,
            prioridad: nuevaPrioridad.toUpperCase(), // BAJA, MEDIA, ALTA
          }
        });
        
        if (response.data.success || response.data.estado) {
          this.mostrarNotificacion(`🚨 Prioridad cambiada de "${prioridadAnterior}" a "${nuevaPrioridad}"`);
          // Recargar tickets desde backend
          await this.cargarTicketsDesdeBackend();
        }
      } catch (error) {
        console.error('❌ Error al actualizar prioridad:', error);
        this.mostrarNotificacion(`❌ Error al actualizar la prioridad`);
        this.$store.dispatch('security/revisaError', error);
        // Recargar para revertir cambio local
        await this.cargarTicketsDesdeBackend();
      }
    },
    
    
    // Método auxiliar para obtener información de prioridad
    obtenerInfoPrioridad(prioridad) {
      switch (prioridad?.toLowerCase()) {
        case 'alta':
          return {
            color: '#FF6600',
            descripcion: 'Prioridad alta - requiere atención rápida',
            tiempoRespuesta: 'Alta (15-30 min)'
          };
        case 'media':
          return {
            color: '#FFCC00',
            descripcion: 'Prioridad media - atención normal',
            tiempoRespuesta: 'Media (30-60 min)'
          };
        case 'baja':
          return {
            color: '#00CC00',
            descripcion: 'Prioridad baja - puede esperar',
            tiempoRespuesta: 'Baja (1-2 horas)'
          };
        default:
          return {
            color: '#FFCC00',
            descripcion: 'Prioridad media por defecto',
            tiempoRespuesta: 'Media (30-60 min)'
          };
      }
    },
    handlePhotoUpload(event) {
      const files = Array.from(event.target.files);
      console.log(`📷 Archivos seleccionados: ${files.length}`);
      
      files.forEach(file => {
        // Validar que sea una imagen
        if (!file.type.startsWith('image/')) {
          alert(`❌ El archivo "${file.name}" no es una imagen válida.`);
          return;
        }
        
        // Validar tamaño (máximo 5MB)
        if (file.size > 5 * 1024 * 1024) {
          alert(`❌ La imagen "${file.name}" es demasiado grande. Máximo 5MB permitido.`);
          return;
        }
        
        // Crear objeto URL para vista previa
        const reader = new FileReader();
        reader.onload = (e) => {
          const foto = {
            nombre: file.name,
            tamaño: file.size,
            tipo: file.type,
            archivo: file,
            url: e.target.result,
            fecha: new Date().toISOString()
          };
          
          // Inicializar array si no existe
          if (!this.ticketDetalle.fotosAdjuntas) {
            this.ticketDetalle.fotosAdjuntas = [];
          }
          
          // Agregar foto al ticket
          this.ticketDetalle.fotosAdjuntas.push(foto);
          
          console.log(`✅ Foto agregada: ${foto.nombre}`);
          this.mostrarNotificacion(`📷 Foto "${foto.nombre}" agregada exitosamente`);
        };
        
        reader.readAsDataURL(file);
      });
      
      // Limpiar el input para permitir seleccionar los mismos archivos nuevamente
      event.target.value = '';
    },
    removePhoto(index) {
      if (this.ticketDetalle.fotosAdjuntas && index >= 0 && index < this.ticketDetalle.fotosAdjuntas.length) {
        const fotoRemovida = this.ticketDetalle.fotosAdjuntas[index];
        this.ticketDetalle.fotosAdjuntas.splice(index, 1);
        
        console.log(`🗑️ Foto removida: ${fotoRemovida.nombre}`);
        this.mostrarNotificacion(`🗑️ Foto "${fotoRemovida.nombre}" removida`);
      }
    },
    mostrarNotificacion(mensaje) {
      // Crear una notificación temporal
      const notificacion = document.createElement('div');
      notificacion.className = 'notificacion-temp';
      notificacion.textContent = mensaje;
      notificacion.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #009a44;
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        z-index: 9999;
        font-size: 0.9rem;
        font-weight: 600;
        animation: slideInRight 0.3s ease-out;
      `;
      
      document.body.appendChild(notificacion);
      
      // Remover la notificación después de 3 segundos
      setTimeout(() => {
        notificacion.style.animation = 'slideOutRight 0.3s ease-in';
        setTimeout(() => {
          if (notificacion.parentNode) {
            notificacion.parentNode.removeChild(notificacion);
          }
        }, 300);
      }, 3000);
    },
    // ===== MÉTODOS DE EJEMPLO PARA USAR LAS NUEVAS RUTAS =====
    
    // Ejemplo: Asignar ticket con interfaz mejorada
    async asignarTicketConInterfaz(ticket) {
      if (!ticket || !ticket.id) {
        this.mostrarNotificacion('❌ Ticket no válido para asignación');
        return;
      }
      
      // Mostrar diálogo para seleccionar técnico
      const tecnicoSeleccionado = prompt(
        `Asignar ticket #${ticket.id} a técnico:\n\n` +
        `Técnicos disponibles:\n${this.listaTecnicos.join('\n')}\n\n` +
        `Ingresa el nombre del técnico:`
      );
      
      if (!tecnicoSeleccionado) return;
      
      // Buscar ID del técnico (esto dependería de cómo esté estructurada la lista)
      const tecnicoId = this.listaTecnicos.indexOf(tecnicoSeleccionado) + 1; // Ejemplo simple
      
      const comentario = prompt('Comentario adicional (opcional):') || '';
      
      const exito = await this.asignarTicket(ticket.id, tecnicoId, comentario);
      if (exito) {
        this.mostrarNotificacion(`✅ Ticket #${ticket.id} asignado a ${tecnicoSeleccionado}`);
      }
    },
    
    // Ejemplo: Mostrar estadísticas generales
    async mostrarEstadisticasGenerales() {
      try {
        const filtros = {
          fechaInicio: prompt('Fecha inicio (YYYY-MM-DD) o dejar vacío:') || undefined,
          fechaFin: prompt('Fecha fin (YYYY-MM-DD) o dejar vacío:') || undefined,
          area: prompt('Área específica o dejar vacío:') || undefined
        };
        
        const estadisticas = await this.obtenerEstadisticas(filtros);
        
        if (estadisticas) {
          const mensaje = `
📊 ESTADÍSTICAS DE TICKETS

📈 Total de tickets: ${estadisticas.totalTickets || 'N/A'}
✅ Resueltos: ${estadisticas.ticketsResueltos || 'N/A'}
⏳ Pendientes: ${estadisticas.ticketsPendientes || 'N/A'}
🔄 En proceso: ${estadisticas.ticketsEnProceso || 'N/A'}
📊 Tasa de resolución: ${estadisticas.tasaResolucion || 'N/A'}%
⏱️ Tiempo promedio: ${estadisticas.tiempoPromedio || 'N/A'}
          `;
          
          alert(mensaje);
        } else {
          this.mostrarNotificacion('❌ No se pudieron obtener las estadísticas');
        }
      } catch (error) {
        console.error('Error al mostrar estadísticas:', error);
        this.mostrarNotificacion('❌ Error al obtener estadísticas');
      }
    },
    
    // Ejemplo: Mostrar historial de un ticket
    async mostrarHistorialTicket(ticketId) {
      if (!ticketId) {
        this.mostrarNotificacion('❌ ID de ticket requerido');
        return;
      }
      
      try {
        const historial = await this.obtenerHistorialTicket(ticketId);
        
        if (historial && historial.length > 0) {
          let mensaje = `📋 HISTORIAL DEL TICKET #${ticketId}\n\n`;
          
          historial.forEach((evento, index) => {
            mensaje += `${index + 1}. ${evento.fecha || 'Sin fecha'}\n`;
            mensaje += `   Acción: ${evento.accion || 'Sin acción'}\n`;
            mensaje += `   Usuario: ${evento.usuario || 'Sin usuario'}\n`;
            mensaje += `   Comentario: ${evento.comentario || 'Sin comentario'}\n\n`;
          });
          
          alert(mensaje);
        } else {
          this.mostrarNotificacion(`ℹ️ No hay historial disponible para el ticket #${ticketId}`);
        }
      } catch (error) {
        console.error('Error al mostrar historial:', error);
        this.mostrarNotificacion('❌ Error al obtener historial');
      }
    },
    
    // Ejemplo: Mostrar estadísticas de un técnico específico
    async mostrarEstadisticasTecnico() {
      const tecnicoId = prompt('Ingresa el ID del técnico:');
      if (!tecnicoId) return;
      
      try {
        const filtros = {
          fechaInicio: prompt('Fecha inicio (YYYY-MM-DD) o dejar vacío:') || undefined,
          fechaFin: prompt('Fecha fin (YYYY-MM-DD) o dejar vacío:') || undefined,
          periodo: prompt('Período (mes/año) o dejar vacío:') || undefined
        };
        
        const estadisticas = await this.obtenerEstadisticasTecnico(tecnicoId, filtros);
        
        if (estadisticas) {
          const mensaje = `
👨‍💼 ESTADÍSTICAS DEL TÉCNICO #${tecnicoId}

   Tickets atendidos: ${estadisticas.ticketsAtendidos || 'N/A'}
   Tickets resueltos: ${estadisticas.ticketsResueltos || 'N/A'}
   Tickets pendientes: ${estadisticas.ticketsPendientes || 'N/A'}
   Eficiencia: ${estadisticas.eficiencia || 'N/A'}%
   Tiempo promedio: ${estadisticas.tiempoPromedio || 'N/A'}
   Calificación: ${estadisticas.calificacion || 'N/A'}
          `;
          
          alert(mensaje);
        } else {
          this.mostrarNotificacion('❌ No se pudieron obtener las estadísticas del técnico');
        }
      } catch (error) {
        console.error('Error al mostrar estadísticas del técnico:', error);
        this.mostrarNotificacion('❌ Error al obtener estadísticas del técnico');
      }
    },
    
    // ===== FIN DE MÉTODOS DE EJEMPLO =====
    
    // Funciones para manejar la reasignación
    async confirmarReasignacion() {
      if (!this.reassignData.razon.trim()) {
        alert('⚠️ Por favor ingresa la razón de la reasignación');
        return;
      }
      
      if (!this.reassignData.nuevaArea) {
        alert('⚠️ Por favor selecciona el área de destino');
        return;
      }
      
      if (this.ticketToReassign) {
        try {
          // Guardar el área anterior para el mensaje
          const areaAnterior = this.getSolicitudText(this.ticketToReassign.solicitudArea);
          const areaNueva = this.getSolicitudText(this.reassignData.nuevaArea);
          
          // Usar el nuevo endpoint específico de transferirArea
          const response = await this.$http({
            url: '/mantenimiento/tickets/transferirArea',
            method: 'POST',
            data: {
              ticketId: this.ticketToReassign.id,
              nuevaArea: this.reassignData.nuevaArea.toUpperCase(),
              motivo: this.reassignData.razon
            }
          });
          
          if (response.data.success || response.data.estado) {
            // Mostrar el mensaje del backend
            this.mostrarNotificacion(response.data.mensaje || `🔄 Ticket reasignado de ${areaAnterior} a ${areaNueva}`);
            
            // Recargar tickets desde backend
            await this.cargarTicketsDesdeBackend();
            
            // Cerrar modal
            this.cerrarModalReasignacion();
          } else {
            // Mostrar error del backend
            alert(`❌ ${response.data.mensaje || 'Error al reasignar el ticket'}`);
          }
        } catch (error) {
          console.error('❌ Error al reasignar ticket:', error);
          const mensajeError = error.response?.data?.mensaje || 'Error al reasignar el ticket';
          this.mostrarNotificacion(`❌ ${mensajeError}`);
          this.$store.dispatch('security/revisaError', error);
        }
      }
    },
    
    cancelarReasignacion() {
      // No revertir el estado, mantener el estado actual
      this.cerrarModalReasignacion();
    },
    
    cerrarModalReasignacion() {
      // Restaurar estado anterior si se canceló la reasignación
      if (this.ticketToReassign && this.ticketToReassign.estadoAnterior) {
        this.ticketToReassign.estado = this.ticketToReassign.estadoAnterior;
      }
      
      this.showReassignModal = false;
      this.ticketToReassign = null;
      this.reassignData.razon = '';
      this.reassignData.nuevaArea = '';
    },
    
    // Funciones para manejar la cancelación
    async confirmarCancelacion() {
      if (!this.cancelData.motivo.trim()) {
        this.mostrarNotificacion('⚠️ Por favor ingresa el motivo de la cancelación');
        return;
      }
      
      if (this.ticketToCancel) {  
        try {
          const response = await this.$http({
            url: '/mantenimiento/tickets/cancelarTicket',
            method: 'POST',
            data: {
              id: this.ticketToCancel.id,
              motivo: this.cancelData.motivo,
            }
          });
          
          if (response.data.success === true || response.data.estado === true) {
            this.mostrarNotificacion(`✅ Ticket cancelado exitosamente`);
            // Recargar tickets desde backend
            await this.cargarTicketsDesdeBackend();
            // Cerrar modal
            this.cerrarModalCancelacion();
          } else {
            const mensaje = response.data.mensaje || 'Error al cancelar el ticket';
            this.mostrarNotificacion(`❌ ${mensaje}`);
          }
        } catch (error) {
          console.error('❌ Error al cancelar ticket:', error);
          this.mostrarNotificacion(`❌ Error al cancelar el ticket`);
          this.$store.dispatch('security/revisaError', error);
        }
      }
    },
    
    cancelarCancelacion() {
      // Revertir el estado del ticket
      if (this.ticketToCancel) {
        this.ticketToCancel.estado = 'pendiente'; // Volver al estado anterior
      }
      this.cerrarModalCancelacion();
    },
    
    cerrarModalCancelacion() {
      this.showCancelModal = false;
      this.ticketToCancel = null;
      this.cancelData.motivo = '';
    },
    
    eliminarTicket(ticketId) {
      if (confirm('¿Estás seguro de que quieres eliminar este ticket?')) {
        this.tickets = this.tickets.filter(t => t.id !== ticketId);
      }
    },
    ///////////////////////////////
    async abrirDetalle(ticket) {
      // Cargar detalle completo desde el backend
      try {
        await this.cargarDetalleTicket(ticket.id);
        this.showTicketDetail = true;
      } catch (error) {
        console.error('Error al abrir detalle:', error);
        this.mostrarNotificacion('❌ Error al cargar el detalle del ticket');
      }
    },
    async cerrarDetalle() {
      const recargar = this.ticketDetalle?.id || null;
      this.ticketDetalle = null;
      this.showTicketDetail = false;
      if (recargar) {
        try {
          await this.cargarTicketsDesdeBackend();
        } catch (error) {
          console.error('❌ Error al recargar tickets después de cerrar detalle:', error);
          this.$store.dispatch('security/revisaError', error);
        }
      }
    },
    
    // Función para reporte normal (usuarios y admins)
    async descargarTicketNormal() {
      if (!this.ticketDetalle) {
        return;
      }

      const fechaDescarga = new Date().toISOString().split('T')[0];
      await this.descargarTicketDesdeBackend({
        url: '/mantenimiento/tickets/generarReporteNormal',
        params: { id: this.ticketDetalle.id },
        nombrePredeterminado: `ticket_${this.ticketDetalle.id}_${fechaDescarga}.pdf`,
        mensajeExito: '📄 Ticket descargado en formato PDF correctamente'
      });
    },

    // Función para reporte completo (solo administradores)
    async descargarTicketCompleto() {
      if (!this.ticketDetalle) {
        return;
      }

      const fechaDescarga = new Date().toISOString().split('T')[0];
      await this.descargarTicketDesdeBackend({
        url: '/mantenimiento/tickets/generarReporteCompleto',
        params: { id: this.ticketDetalle.id },
        nombrePredeterminado: `ticket_completo_${this.ticketDetalle.id}_${fechaDescarga}.pdf`,
        mensajeExito: '📋 Reporte completo descargado exitosamente'
      });
    },
    async descargarTicketDesdeBackend({
      url,
      params = {},
      nombrePredeterminado = 'reporte.pdf',
      mensajeExito = ''
    }) {
      if (!this.ticketDetalle) {
        return;
      }

      try {
        const response = await this.$http({
          url,
          method: 'GET',
          params,
          responseType: 'arraybuffer',
          headers: {
            Accept: 'application/pdf'
          }
        });

        this.descargarBlobComoArchivo(response.data, response.headers, nombrePredeterminado);
        if (mensajeExito) {
          this.mostrarNotificacion(mensajeExito);
        }
      } catch (error) {
        console.error('❌ Error al descargar el PDF del ticket:', error);
        this.mostrarNotificacion('❌ Error al descargar el PDF del ticket');
        this.$store.dispatch('security/revisaError', error);
      }
    },

    descargarBlobComoArchivo(data, headers = {}, nombrePredeterminado = 'reporte.pdf') {
      if (!data) {
        return;
      }

      const contentType = headers['content-type'] || headers['Content-Type'] || 'application/pdf';
      let nombreArchivo = nombrePredeterminado;

      const contentDisposition = headers['content-disposition'] || headers['Content-Disposition'];
      if (contentDisposition) {
        const filenameMatch = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(contentDisposition);
        if (filenameMatch) {
          try {
            nombreArchivo = decodeURIComponent(filenameMatch[1] || filenameMatch[2] || nombrePredeterminado);
      } catch (error) {
            nombreArchivo = filenameMatch[1] || filenameMatch[2] || nombrePredeterminado;
          }
        }
      }

      const blob = new Blob([data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = nombreArchivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    
    guardarSolucionTecnico() {
      if (!this.ticketDetalle) return;
      
      // Actualizar la fecha de actualización
      this.ticketDetalle.fechaActualizacionSolucion = new Date().toISOString();
      
      // Encontrar el ticket en la lista y actualizarlo
      const ticketIndex = this.tickets.findIndex(t => t.id === this.ticketDetalle.id);
      if (ticketIndex !== -1) {
        // Actualizar el ticket en la lista
        this.tickets[ticketIndex] = { ...this.ticketDetalle };
      }
      
      // Mostrar notificación de éxito
      this.mostrarNotificacion('✅ Solución del técnico guardada exitosamente');
      
      // solucion del tecnico
      console.log('Solución del técnico guardada:', {
        ticketId: this.ticketDetalle.id,
        //problemaIdentificado: this.ticketDetalle.problemaIdentificado,
        //solucionAplicada: this.ticketDetalle.solucionAplicada,
        //herramientasUtilizadas: this.ticketDetalle.herramientasUtilizadas,
        //tiempoRealSolucion: this.ticketDetalle.tiempoRealSolucion,
        observacionesTecnico: this.ticketDetalle.observacionesTecnico,
        fechaActualizacionSolucion: this.ticketDetalle.fechaActualizacionSolucion
      });
    },
    
    // Agregar nueva solución al feed
    async agregarSolucion() {
      if (!this.ticketDetalle || !this.nuevaSolucion.solucion.trim()) return;
      
      // Validar que no se puedan agregar soluciones en estado pendiente
      if (this.ticketDetalle.estado === 'pendiente') {
        alert('❌ No se pueden agregar soluciones a tickets pendientes. Cambia el estado a "En Proceso" primero.');
        return;
      }
      
      // Validar que no se pueda agregar seguimiento si el ticket no está resuelto
      if (this.nuevaSolucion.tipo === 'seguimiento' && this.ticketDetalle.estado !== 'resuelto') {
        alert('❌ Solo se pueden agregar seguimientos a tickets resueltos.');
        return;
      }
      
      // Validar que no se pueda agregar solución inicial si el ticket está resuelto
      if (this.nuevaSolucion.tipo === 'solucion_inicial' && this.ticketDetalle.estado === 'resuelto') {
        alert('❌ No se pueden agregar soluciones a tickets resueltos. Solo se permite seguimiento.');
        return;
      }
      
      try {
        // Determinar el endpoint según el tipo de solución
        const url = this.nuevaSolucion.tipo === 'seguimiento'
          ? '/mantenimiento/tickets/agregarSolucionSeguimiento'
          : '/mantenimiento/tickets/agregarSolucionTecnico';
        
        const response = await this.$http({
          url: url,
          method: 'POST',
          data: {
            ticketId: this.ticketDetalle.id,
            solucion: this.nuevaSolucion.solucion.trim(),
          }
        });
        
        if (response.data.success || response.data.estado) {
          // Si hay imagenes, subirlas por separado con el solucionId retornado 
          if (this.nuevaSolucion.imagenes && this.nuevaSolucion.imagenes.length > 0) {
		const solucionId = response.data.solucionId; 
    if(solucionId){

            await this.subirImagenesSolucion(this.ticketDetalle.id,solucionId,this.nuevaSolucion.tipo);
             }else{
		console.warn('No se recibio solucionId del backend, las imagenes no se subiran')
  }
}

          this.mostrarNotificacion('💬 Solución agregada exitosamente');
          
          // Recargar el detalle del ticket desde el backend
          await this.cargarDetalleTicket(this.ticketDetalle.id);
          
          // Limpiar el formulario
          this.limpiarFormularioSolucion();
        }
      } catch (error) {
        console.error('❌ Error al agregar solución:', error);
        this.mostrarNotificacion('❌ Error al agregar la solución');
        this.$store.dispatch('security/revisaError', error);
      }
    },
    
    // Cargar detalle del ticket desde backend
    async cargarDetalleTicket(ticketId) {
      try {
        const response = await this.$http({
          url: '/mantenimiento/tickets/obtenerTicket',
          method: 'GET',
          params: { id: ticketId }
        });
        
        if (response.data) {
          const detalle = response.data;
          const fechaCreacionDate = this.parseFechaFlexible(detalle.fechaCreacion);
          const fechaAsignacionDate = this.parseFechaFlexible(detalle.fechaAsignacion);
          const fechaCierreDate = this.parseFechaFlexible(detalle.fechaCierre);

          // Procesar imágenes del backend
          const imagenesTecnicoProcesadas = this.transformarImagenesSolucion(detalle.imagenesTecnico || []);
          const imagenesSeguimientoProcesadas = this.transformarImagenesSolucion(detalle.imagenesSeguimiento || []);
          
          // Log para depuración
          console.log('📸 Imágenes recibidas del backend:', {
            imagenesTecnico: detalle.imagenesTecnico,
            imagenesSeguimiento: detalle.imagenesSeguimiento,
            imagenesTecnicoProcesadas: imagenesTecnicoProcesadas,
            imagenesSeguimientoProcesadas: imagenesSeguimientoProcesadas
          });

          this.ticketDetalle = {
            ...detalle,
            solicitudArea: detalle.solicitudArea?.toLowerCase(),
            prioridad: this.capitalizarPrioridad(detalle.prioridad),
            estado: detalle.estado?.toLowerCase(),
            tecnico: detalle.tecnicoAsignado?.nombre || detalle.tecnico || 'Sin asignar',
            tecnicoAsignadoId: detalle.tecnicoAsignado?.id ?? null,
            fechaCreacion: fechaCreacionDate ? fechaCreacionDate.toISOString() : detalle.fechaCreacion,
            fechaAsignacion: fechaAsignacionDate ? fechaAsignacionDate.toISOString() : detalle.fechaAsignacion,
            fechaCierre: fechaCierreDate ? fechaCierreDate.toISOString() : detalle.fechaCierre,
            fechaCreacionTexto: detalle.fechaCreacion,
            fechaAsignacionTexto: detalle.fechaAsignacion,
            fechaCierreTexto: detalle.fechaCierre,
            // Guardar imágenes procesadas para usar en el historial
            imagenesTecnico: imagenesTecnicoProcesadas,
            imagenesSeguimiento: imagenesSeguimientoProcesadas,
          };
          await this.cargarHistorialSoluciones(ticketId);
        }
      } catch (error) {
        console.error('❌ Error al cargar detalle del ticket:', error);
        this.$store.dispatch('security/revisaError', error);
      }
    },
    async cargarHistorialSoluciones(ticketId) {
      this.historialSoluciones = [];
      try {
        const response = await this.$http({
          url: `/mantenimiento/tickets/obtenerHistorial/${ticketId}`,
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        const eventos = response.data?.root || [];
        
        // Obtener imágenes del ticket detalle como respaldo
        const imagenesTecnicoDelTicket = this.ticketDetalle?.imagenesTecnico || [];
        const imagenesSeguimientoDelTicket = this.ticketDetalle?.imagenesSeguimiento || [];
        
        // Log para depuración
        console.log('📋 Historial recibido:', {
          eventosCount: eventos.length,
          imagenesTecnicoDelTicket: imagenesTecnicoDelTicket.length,
          imagenesSeguimientoDelTicket: imagenesSeguimientoDelTicket.length,
          eventos: eventos.map(e => ({
            tipo: e.tipo,
            tieneImagenes: !!e.imagenes,
            imagenesCount: e.imagenes ? (Array.isArray(e.imagenes) ? e.imagenes.length : 1) : 0
          }))
        });
        
        const soluciones = eventos
          .filter(evento => evento && (evento.tipo === 'SOLUCION' || evento.tipo === 'SEGUIMIENTO'))
          .map((evento, index) => {
            const fechaParsed = this.parseFechaFlexible(evento.fecha) || new Date();
            const tipo = evento.tipo === 'SOLUCION' ? 'solucion_inicial' : 'seguimiento';
            const tiempoMin = evento.tiempoMinutos ?? evento.tiempoDedicadoMinutos;
            let tiempoTexto = '';
            if (Number.isFinite(Number(tiempoMin)) && Number(tiempoMin) > 0) {
              const minutos = Number(tiempoMin);
              if (minutos >= 60) {
                tiempoTexto = `${(minutos / 60).toFixed(1)} horas dedicadas`;
              } else {
                tiempoTexto = `${minutos} minutos dedicados`;
              }
            }
            const accionLabel = evento.accion && evento.accion !== 'SOLUCION_TECNICA'
              ? evento.accion.replace(/_/g, ' ')
              : '';
            
            // Procesar imágenes: primero las del evento, luego las del ticket detalle como respaldo
            let imagenesDelEvento = this.transformarImagenesSolucion(evento.imagenes);
            let imagenesCombinadas = [];
            
            // Prioridad: usar imágenes del evento si existen
            if (imagenesDelEvento && imagenesDelEvento.length > 0) {
              imagenesCombinadas = imagenesDelEvento;
            } else {
              // Si no hay imágenes en el evento, usar las del ticket detalle según el tipo
              if (tipo === 'solucion_inicial' && imagenesTecnicoDelTicket.length > 0) {
                imagenesCombinadas = imagenesTecnicoDelTicket;
              } else if (tipo === 'seguimiento' && imagenesSeguimientoDelTicket.length > 0) {
                imagenesCombinadas = imagenesSeguimientoDelTicket;
              }
            }
            
            return {
              id: evento.id || `${evento.tipo}-${fechaParsed.getTime()}-${index}`,
              tipo,
              fecha: fechaParsed.toISOString(),
              tecnico: evento.usuario || 'Usuario desconocido',
              solucion: evento.descripcion || evento.solucionTecnico || evento.solucionSeguimiento || 'Sin descripción',
              accion: evento.accion || null,
              accionLabel,
              exitosa: typeof evento.exitosa !== 'undefined' ? evento.exitosa : null,
              tiempoMinutos: tiempoMin || null,
              tiempoTexto: tiempoTexto || '',
              imagenes: imagenesCombinadas
            };
          })
          .sort((a, b) => new Date(a.fecha) - new Date(b.fecha));

        // Si no hay eventos en el historial pero hay imágenes en el ticket detalle,
        // crear entradas para mostrar las imágenes (incluso si no hay texto de solución)
        if (soluciones.length === 0) {
          // Si hay imágenes técnicas, crear entrada (con o sin texto de solución)
          if (this.ticketDetalle?.imagenesTecnico && this.ticketDetalle.imagenesTecnico.length > 0) {
            soluciones.push({
              id: `solucion-tecnico-${ticketId}`,
              tipo: 'solucion_inicial',
              fecha: this.ticketDetalle.fechaAsignacion || this.ticketDetalle.fechaCreacion || new Date().toISOString(),
              tecnico: this.ticketDetalle.tecnico || 'Usuario desconocido',
              solucion: this.ticketDetalle.solucionTecnico || 'Solución técnica con imágenes',
              accion: null,
              accionLabel: '',
              exitosa: null,
              tiempoMinutos: null,
              tiempoTexto: '',
              imagenes: this.ticketDetalle.imagenesTecnico
            });
          }
          
          // Si hay imágenes de seguimiento, crear entrada (con o sin texto de solución)
          if (this.ticketDetalle?.imagenesSeguimiento && this.ticketDetalle.imagenesSeguimiento.length > 0) {
            soluciones.push({
              id: `solucion-seguimiento-${ticketId}`,
              tipo: 'seguimiento',
              fecha: this.ticketDetalle.fechaCierre || this.ticketDetalle.fechaAsignacion || new Date().toISOString(),
              tecnico: this.ticketDetalle.tecnico || 'Usuario desconocido',
              solucion: this.ticketDetalle.solucionSeguimiento || 'Seguimiento con imágenes',
              accion: null,
              accionLabel: '',
              exitosa: null,
              tiempoMinutos: null,
              tiempoTexto: '',
              imagenes: this.ticketDetalle.imagenesSeguimiento
            });
          }
        }

        this.historialSoluciones = soluciones;
        if (this.ticketDetalle) {
          this.ticketDetalle.solucionesTecnico = soluciones;
        }
        
        // Log final para verificar que las imágenes se asignaron correctamente
        console.log('✅ Soluciones procesadas:', {
          totalSoluciones: soluciones.length,
          solucionesConImagenes: soluciones.filter(s => s.imagenes && s.imagenes.length > 0).length,
          soluciones: soluciones.map(s => ({
            id: s.id,
            tipo: s.tipo,
            imagenesCount: s.imagenes ? s.imagenes.length : 0,
            imagenes: s.imagenes ? s.imagenes.map(img => ({ nombre: img.nombre, url: img.url?.substring(0, 50) + '...' })) : []
          }))
        });
      } catch (error) {
        console.warn('⚠️ No se pudo cargar el historial de soluciones:', error);
        // Si falla el historial pero tenemos imágenes en el ticket detalle, crear entradas básicas
        if (this.ticketDetalle) {
          const solucionesFallback = [];
          
          // Crear entrada si hay imágenes técnicas (con o sin texto)
          if (this.ticketDetalle.imagenesTecnico && this.ticketDetalle.imagenesTecnico.length > 0) {
            solucionesFallback.push({
              id: `solucion-tecnico-${ticketId}`,
              tipo: 'solucion_inicial',
              fecha: this.ticketDetalle.fechaAsignacion || this.ticketDetalle.fechaCreacion || new Date().toISOString(),
              tecnico: this.ticketDetalle.tecnico || 'Usuario desconocido',
              solucion: this.ticketDetalle.solucionTecnico || 'Solución técnica con imágenes',
              accion: null,
              accionLabel: '',
              exitosa: null,
              tiempoMinutos: null,
              tiempoTexto: '',
              imagenes: this.ticketDetalle.imagenesTecnico
            });
          }
          
          // Crear entrada si hay imágenes de seguimiento (con o sin texto)
          if (this.ticketDetalle.imagenesSeguimiento && this.ticketDetalle.imagenesSeguimiento.length > 0) {
            solucionesFallback.push({
              id: `solucion-seguimiento-${ticketId}`,
              tipo: 'seguimiento',
              fecha: this.ticketDetalle.fechaCierre || this.ticketDetalle.fechaAsignacion || new Date().toISOString(),
              tecnico: this.ticketDetalle.tecnico || 'Usuario desconocido',
              solucion: this.ticketDetalle.solucionSeguimiento || 'Seguimiento con imágenes',
              accion: null,
              accionLabel: '',
              exitosa: null,
              tiempoMinutos: null,
              tiempoTexto: '',
              imagenes: this.ticketDetalle.imagenesSeguimiento
            });
          }
          
          if (solucionesFallback.length > 0) {
            console.log('✅ Creando soluciones de respaldo con imágenes:', solucionesFallback);
            this.historialSoluciones = solucionesFallback;
            this.ticketDetalle.solucionesTecnico = solucionesFallback;
          }
        }
      }
    },
    
    // Subir imágenes de la solución
    async subirImagenesSolucion(ticketId,solucionId, tipoSolucion) {
      const endpoint = tipoSolucion === 'seguimiento'
        ? '/mantenimiento/tickets/subirImagenSolucionSeguimiento'
        : '/mantenimiento/tickets/subirImagenSolucionTecnico';
      
      for (const imagen of this.nuevaSolucion.imagenes) {
        try {
          const formData = new FormData();
          formData.append('imagen', imagen.archivo);
          formData.append('ticketId', ticketId);
          formData.append('solucionId',solucionId);

          await this.$http({
            url: endpoint,
            method: 'POST', 
            data: formData,
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
        } catch (error) {
          console.error('Error al subir imagen:', error);
        }
      }
    },
    
    transformarImagenesSolucion(imagenes) {
      if (!imagenes) {
        return [];
      }
      
      const listaImagenes = Array.isArray(imagenes)
        ? imagenes
        : typeof imagenes === 'object'
          ? Object.values(imagenes)
          : [];
      
      if (!listaImagenes.length) {
        return [];
      }
      
      return listaImagenes
        .map((imagen, index) => {
          if (!imagen) {
            return null;
          }
          
          // Caso 1: Si es un string simple (URL), procesarlo primero
          if (typeof imagen === 'string') {
            const url = this.construirUrlImagen(imagen);
            if (!url) {
              return null;
            }
            const nombre = this.extraerNombreArchivo(imagen) || `Imagen ${index + 1}`;
            return {
              nombre,
              url
            };
          }
          
          // Caso 2: Ya viene formateada con url y nombre
          if (imagen.url && imagen.nombre) {
            return {
              nombre: imagen.nombre,
              url: this.construirUrlImagen(imagen.url)
            };
          }
          
          // Obtener nombre amigable
          let nombre = imagen.nombre
            || imagen.nombreOriginal
            || imagen.filename
            || imagen.nombreArchivo
            || `Imagen ${index + 1}`;
          
          let url = null;
          
          // 1. Si trae un campo url/route
          const posibleRuta = imagen.url || imagen.enlace || imagen.link || imagen.ruta || imagen.path;
          if (posibleRuta) {
            url = this.construirUrlImagen(posibleRuta);
            if (!nombre || nombre === `Imagen ${index + 1}`) {
              nombre = this.extraerNombreArchivo(posibleRuta) || nombre;
            }
          }
          
          // 2. Si trae base64 embebido
          if (!url) {
            const posibleBase64 = imagen.base64 || imagen.contenido || imagen.data || imagen.contenidoBase64;
            if (posibleBase64) {
              url = posibleBase64.startsWith('data:')
                ? posibleBase64
                : `data:${imagen.tipoContenido || imagen.contentType || 'image/jpeg'};base64,${posibleBase64}`;
            }
          }
          
          // 3. Si trae un id, construir endpoint estándar
          if (!url) {
            const posibleId = imagen.id || imagen.imagenId || imagen.archivoId;
            if (posibleId) {
              const endpoint = imagen.endpointDescarga || `/mantenimiento/tickets/imagenSolucion/${posibleId}`;
              url = this.construirUrlImagen(endpoint);
            }
          }
          
          if (!url) {
            console.warn('⚠️ No se pudo procesar imagen:', imagen);
            return null;
          }
          
          if (!nombre || nombre === `Imagen ${index + 1}`) {
            nombre = this.extraerNombreArchivo(url) || `Imagen ${index + 1}`;
          }
          
          return {
            nombre,
            url
          };
        })
        .filter(Boolean);
    },
    
    construirUrlImagen(ruta) {
      if (!ruta) {
        return null;
      }
      
      // Si ya es data URL, blob o http(s), regresarlo tal cual
      if (/^(data:|blob:|https?:\/\/)/i.test(ruta)) {
        return ruta;
      }
      
      const baseURL = this.$http?.defaults?.baseURL || '';
      const baseNormalizada = baseURL.replace(/\/$/, '');
      const rutaNormalizada = ruta.startsWith('/') ? ruta : `/${ruta}`;
      
      return `${baseNormalizada}${rutaNormalizada}`;
    },
    
    extraerNombreArchivo(ruta) {
      if (!ruta || typeof ruta !== 'string') {
        return '';
      }
      const sinParametros = ruta.split('?')[0];
      const partes = sinParametros.split('/');
      return partes.pop() || '';
    },
    
    // Limpiar formulario de nueva solución
            limpiarFormularioSolucion() {
              this.nuevaSolucion = {
                solucion: '',
                tipo: 'solucion_inicial',
                imagenes: []
              };
            },
    
    // Obtener texto del tipo de solución
    getTipoSolucionText(tipo) {
      const tipos = {
        'solucion_inicial': 'Solución',
        'seguimiento': 'Seguimiento'
      };
      return tipos[tipo] || tipo;
    },
    
    // Formatear fecha y hora para el feed
            formatearFechaHora(fecha) {
              const fechaObj = new Date(fecha);
              return fechaObj.toLocaleString('es-ES', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              });
            },

            // Métodos para manejar imágenes en soluciones
            handleSolutionImageUpload(event) {
              const files = Array.from(event.target.files);
              
              files.forEach(file => {
                if (file.type.startsWith('image/')) {
                  const reader = new FileReader();
                  reader.onload = (e) => {
                    this.nuevaSolucion.imagenes.push({
                      nombre: file.name,
                      url: e.target.result,
                      archivo: file
                    });
                  };
                  reader.readAsDataURL(file);
                }
              });
            },

            removeSolutionImage(index) {
              this.nuevaSolucion.imagenes.splice(index, 1);
            },

            openImageModal(imagen) {
              this.imagenModal = {
                visible: true,
                imagen: imagen
              };
            },

            closeImageModal() {
              this.imagenModal = {
                visible: false,
                imagen: { nombre: '', url: '' }
              };
            },
    
    abrirFormulario() {
      this.showFormModal = true;
    },
    cerrarFormulario() {
      this.showFormModal = false;
      // Limpiar formulario al cerrar
      this.form = {
        nombre: this.userName || this.form.nombre, // Mantener el nombre del usuario
        titulo: '',
        descripcion: '',
        areaSolicita: '',
        ubicacion: '',
        equipo: '',
        tieneEquipo: false,
        descripcionEquipo: '',
        tieneInsumos: false,
        insumos: '',
        descripcionInsumos: '',
        cantidadInsumos: 1,
        solicitudArea: '',
        serialNumber: '',
        notas: '',
        fechaCreacion: '',
        fechaCierre: '',
      };
    },
    cerrarTicketsCreadosResueltos() {
      this.showTicketsCreadosResueltos = false;
    },
    cerrarTiempoPromedio() {
      this.showTiempoPromedio = false;
    },
    cerrarTicketsPorTecnico() {
      this.showTicketsPorTecnico = false;
    },
    cerrarTicketsPorArea() {
      this.showTicketsPorArea = false;
    },

     cerrarProblemasMasComunes() {
      this.showProblemasMasComunes = false;
    },

    limpiarFiltros() {
      this.filtroArea = '';
      this.filtroPrioridad = '';
      this.filtroEstado = 'activos';
    },

     
    
    // Función para verificar si un usuario puede cancelar un ticket
    puedeCancelarTicket(ticket) {
      if (!ticket) {
        return false;
      }
      const estado = (ticket.estado || '').toString().toLowerCase();
      if (!(estado === 'pendiente')) {
        return false;
      }

      const creadorNombre = (ticket.usuarioCreador && ticket.usuarioCreador.nombre)
        ? ticket.usuarioCreador.nombre.toString().trim().toLowerCase()
        : (ticket.usuarioCreadorNombre
          ? ticket.usuarioCreadorNombre.toString().trim().toLowerCase()
          : null);
      const usuarioActualNombre = (this.$store.state.security.username || '').toString().trim().toLowerCase();

      return creadorNombre && usuarioActualNombre && creadorNombre === usuarioActualNombre;
    },
    
    // Función para abrir el modal de cancelación
    abrirModalCancelacion(ticket) {
      this.ticketToCancel = ticket;
      this.cancelData.motivo = '';
      this.showCancelModal = true;
    },
    getEstadoClass(estado) {
      switch (estado) {
        case 'pendiente':
          return 'badge-pending';
        case 'en_proceso':
          return 'badge-in-progress';
        case 'resuelto':
          return 'badge-resolved';
        case 'reasignado':
          return 'badge-reassigned';
        case 'reasignar':
          return 'badge-reassign';
        case 'cancelado':
          return 'badge-cancelled';
        case 'cancelar':
          return 'badge-cancel';
        default:
          return '';
      }
    },
    getEstadoText(estado, esAdmin = false) {
      switch (estado) {
        case 'pendiente':
          return 'Pendiente';
        case 'en_proceso':
          return 'En Proceso';
        case 'resuelto':
          return 'Resuelto';
        case 'reasignado':
          return 'Reasignado';
        case 'reasignar':
          return esAdmin ? 'Reasignar' : 'Reasignado';
        case 'cancelado':
          return 'Cancelado';
        case 'cancelar':
          return esAdmin ? 'Cancelar' : 'Cancelado';
        default:
          return estado;
      }
    },
    getSolicitudText(solicitud) {
      if (!solicitud) return 'Sin área';
      const sol = solicitud.toLowerCase();
      switch (sol) {
        case 'tecnologia':
        case 'tecnologias':
          return 'Tecnología';
        case 'mantenimiento':
          return 'Mantenimiento';
        case 'biomedica':
          return 'Biomédica';
        default:
          return solicitud;
      }
    },
    
    // Función para generar el folio según el área
    generarFolio(ticket) {
      if (!ticket) return '-';
      
      // Si el ticket ya tiene folio del backend, usarlo directamente
      if (ticket.folio) {
        return ticket.folio;
      }
      
      // Fallback: Generar folio localmente si no existe
      let prefijo = '';
      const area = ticket.solicitudArea?.toLowerCase();
      
      switch (area) {
        case 'tecnologias': // Corregido: era 'tecnologia'
          prefijo = 'Tec';
          break;
        case 'biomedica':
          prefijo = 'Bio';
          break;
        case 'mantenimiento':
          prefijo = 'Mtt';
          break;
        default:
          prefijo = 'Gen'; // General para tickets sin área específica
      }
      
      // Formatear el número con ceros a la izquierda (4 dígitos)
      const numero = String(ticket.id).padStart(4, '0');
      
      return `${prefijo}-${numero}`;
    },
    parseFechaFlexible(valor) {
      if (!valor || valor === '' || valor === 'null') {
        return null;
      }

      if (valor instanceof Date) {
        return isNaN(valor.getTime()) ? null : valor;
      }

      if (typeof valor === 'number') {
        const fechaNumero = new Date(valor);
        return isNaN(fechaNumero.getTime()) ? null : fechaNumero;
      }

       // Trim only for strings
      const valorTrim = typeof valor === 'string' ? valor.trim() : valor;

      // Detectar si es timestamp ISO (con T)
      if (typeof valor === 'string' && valorTrim.includes('T')) {
        const directa = new Date(valorTrim);
        if (!isNaN(directa.getTime())) {
          return directa;
        }
      }

      // Detectar si es string con espacio (fecha y hora separadas)
      if (typeof valor === 'string' && valorTrim.includes(' ')) {
        const iso = valorTrim.replace(' ', 'T');
        const directa = new Date(iso);
        if (!isNaN(directa.getTime())) {
          return directa;
        }
      }

        
        const partes = valorTrim.split(' ');
        if (partes.length >= 1) {
          const fechaPart = partes[0];
          const horaPart = partes.length > 1 ? partes[1] : '00:00';
          const separador = fechaPart.includes('/') ? '/' : '-';
          const componentes = fechaPart.split(separador);
          if (componentes.length === 3) {
            let anio, mes, dia;
            if (componentes[0].length === 4) {
              // yyyy-MM-dd
              anio = parseInt(componentes[0], 10);
              mes = parseInt(componentes[1], 10) - 1;
              dia = parseInt(componentes[2], 10);
            } else {
              // dd-MM-yyyy
              dia = parseInt(componentes[0], 10);
              mes = parseInt(componentes[1], 10) - 1;
              anio = parseInt(componentes[2], 10);
            }

            const [horaStr = '0', minutoStr = '0'] = horaPart.split(':');
            const hora = parseInt(horaStr, 10);
            const minuto = parseInt(minutoStr, 10);

            const fechaConstruida = new Date(anio, mes, dia, hora, minuto);
            if (!isNaN(fechaConstruida.getTime())) {
              return fechaConstruida;
            }
          }
        
      }

      return null;
    },
    esFechaPosterior(fechaInicial, fechaFinal) {
      const inicio = this.parseFechaFlexible(fechaInicial);
      const fin = this.parseFechaFlexible(fechaFinal);
      if (!inicio || !fin) {
        return false;
      }
      return fin.getTime() >= inicio.getTime();
    },
    formatearFecha(fechaISO) {
      // Validar que la fecha no sea null, undefined o string vacío
      if (!fechaISO || fechaISO === '' || fechaISO === 'null') {
        return 'Sin fecha';
      }
      
      try {
        const fecha = this.parseFechaFlexible(fechaISO);
        if (!fecha) {
          return 'Sin fecha';
        }
        
        return fecha.toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (e) {
        console.error('Error al formatear fecha:', fechaISO, e);
        return 'Sin fecha';
      }
    },
    formatearFechaCorta(fechaISO) {
      // Validar que la fecha no sea null, undefined o string vacío
      if (!fechaISO || fechaISO === '' || fechaISO === 'null') {
        return '-';
      }
      
      try {
        const fecha = this.parseFechaFlexible(fechaISO);
        if (!fecha) {
          return '-';
        }
        
        return fecha.toLocaleDateString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      } catch (e) {
        console.error('Error al formatear fecha:', fechaISO, e);
        return '-';
      }
    },
    formatearFechaHoraCorta(fechaISO) {
      // Validar que la fecha no sea null, undefined o string vacío
      if (!fechaISO || fechaISO === '' || fechaISO === 'null') {
        return { fecha: 'Sin fecha', hora: '' };
      }
      
      try {
        const fecha = this.parseFechaFlexible(fechaISO);
        if (!fecha) {
          return { fecha: 'Sin fecha', hora: '' };
        }
        
        const fechaStr = fecha.toLocaleDateString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
        const horaStr = fecha.toLocaleTimeString('es-ES', {
          hour: '2-digit',
          minute: '2-digit'
        });
        return { fecha: fechaStr, hora: horaStr };
      } catch (e) {
        console.error('Error al formatear fecha:', fechaISO, e);
        return { fecha: 'Sin fecha', hora: '' };
      }
    },
    calcularTiempoResolucion(fechaCreacion, fechaCierre) {
      const fechaInicio = this.parseFechaFlexible(fechaCreacion);
      const fechaFin = this.parseFechaFlexible(fechaCierre);
      if (!fechaInicio || !fechaFin) return 'Sin tiempo de resolución';

      const diferencia = fechaFin.getTime() - fechaInicio.getTime();
      if (diferencia <= 0) {
        return 'Sin tiempo de resolución';
      }

      const horas = diferencia / (1000 * 60 * 60);
      
      if (horas < 1) {
        const minutos = Math.round(horas * 60);
        return `${minutos} minutos`;
      } else if (horas < 24) {
        return `${horas.toFixed(1)} horas`;
      } else {
        const dias = Math.floor(horas / 24);
        const horasRestantes = horas % 24;
        return `${dias} días y ${horasRestantes.toFixed(1)} horas`;
      }
    },
    incrementarCantidad() {
      const cantidadActual = parseInt(this.form.cantidadInsumos) || 0;
      if (cantidadActual < 999) {
        this.form.cantidadInsumos = cantidadActual + 1;
      }
    },
    decrementarCantidad() {
      const cantidadActual = parseInt(this.form.cantidadInsumos) || 0;
      if (cantidadActual > 1) {
        this.form.cantidadInsumos = cantidadActual - 1;
      }
    },
    // Métodos para autocompletado de insumos
    async buscarSugerenciasInsumos() {
      // Si el texto es muy corto, no buscar
      if (!this.form.insumos || this.form.insumos.length < 2) {
        this.sugerenciasInsumos = [];
        this.mostrarSugerencias = false;
        return;
      }

      try {
        const areaActual = this.form.solicitudArea || this.form.areaRequerida || this.form.area || null;
        // Usar el nuevo servicio que se conecta al backend
        // Esto busca en la tabla catalogo_insumos de la base de datos
        this.sugerenciasInsumos = await InsumosService.buscarInsumos(this.form.insumos, 10, areaActual);
        this.mostrarSugerencias = this.sugerenciasInsumos.length > 0;
      } catch (error) {
        console.error('Error al buscar sugerencias de insumos:', error);
        this.sugerenciasInsumos = [];
        this.mostrarSugerencias = false;
      }
    },
    seleccionarSugerencia(sugerencia) {
      this.form.insumos = sugerencia.textoCompleto || sugerencia.descripcion;
      this.form.descripcionInsumos = InsumosService.formatearDetalles(sugerencia);
      this.mostrarSugerencias = false;
      this.sugerenciasInsumos = [];
    },
    ocultarSugerencias() {
      // Pequeño delay para permitir que se ejecute el click en las sugerencias
      setTimeout(() => {
        this.mostrarSugerencias = false;
      }, 200);
    },
    

    
    getPrioridadStyle(ticket) {
      if (ticket.prioridadColor) {
        return {
          borderLeft: `4px solid ${ticket.prioridadColor}`,
          backgroundColor: `${ticket.prioridadColor}10`
        };
      }
      return {};
    },
    
    getPrioridadBadgeStyle(ticket) {
      const color = ticket.prioridadColor || '#FFCC00';
      return {
        backgroundColor: color,
        color: 'white',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '0.8rem',
        fontWeight: 'bold',
        display: 'inline-block',
        minWidth: '60px',
        textAlign: 'center'
      };
    },
    

  },
  watch: {
    role: {
      immediate: true,
      handler() {
        if (!this.esAdminOTecnico) {
          if (!this.form.nombre) {
            this.form.nombre = '';
          }
        }
      },
    },
    userName: {
      immediate: true,
      handler(newUserName) {
        if (newUserName) {
          this.form.nombre = newUserName;
        }
      },
    },
    'form.solicitudArea': {
      handler(newArea) {
        const areaNormalizada = (newArea || '').toString().trim().toUpperCase();
        // Limpiar sugerencias únicamente cuando se cambia a un área distinta de Biomédica/Biomedica
        if (areaNormalizada && areaNormalizada !== 'BIOMEDICA') {
          this.sugerenciasInsumos = [];
          this.mostrarSugerencias = false;
        }
      }
    }
  },
};
</script>

<style scoped>
.ticket-form-container {
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 10px 0;
  margin: 0;
  font-family: 'Montserrat', sans-serif;
  box-sizing: border-box;
  overflow-x: hidden;
}


.main-content {
  width: 95%;
  max-width: 1400px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.table-header h3 {
  margin: 0;
  color: #009a44;
  font-size: 1.3rem;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 15px;
  align-items: center;
}

.new-ticket-btn {
  background: #009a44;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.new-ticket-btn:hover {
  background: #007a36;
}

.new-ticket-btn:active {
  background: #006a2e;
}

.header-with-actions {
  width: 90%;
  max-width: 1200px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  background: #fff;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  box-sizing: border-box;
}

.header-with-actions h2 {
  margin: 0;
  font-size: 1.5rem;
}

.admin-actions {
  position: relative;
}

.dropdown {
  position: relative;
}

.stats-btn {
  background: #4a90e2;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 10px 18px;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: background 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 4px 10px rgba(74, 144, 226, 0.2);
}

.stats-btn:hover {
  background: #1f5fbf;
  box-shadow: 0 6px 14px rgba(31, 95, 191, 0.28);
}

.stats-toggle-btn {
  font-weight: 600;
  gap: 12px;
  min-width: 170px;
  justify-content: center;
}

.stats-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
  padding: 12px 0;
}

.stats-option-btn {
  background: #ffffff;
  color: #1f2a44;
  border: 1px solid #d0d8e6;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 0.95rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.stats-option-btn:hover {
  background: linear-gradient(135deg, #edf3ff 0%, #dbe8ff 100%);
  border-color: #4a90e2;
  box-shadow: 0 10px 24px rgba(74, 144, 226, 0.18);
  transform: translateY(-1px);
}

.stats-option-btn:active {
  transform: translateY(0);
  box-shadow: 0 6px 14px rgba(74, 144, 226, 0.14);
}

.dropdown-arrow {
  font-size: 0.8rem;
  transition: transform 0.2s;
}

.dropdown-arrow.open {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  z-index: 1000;
  min-width: 200px;
}

.dropdown-menu button {
  display: block;
  width: 100%;
  padding: 8px 16px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background 0.2s;
}

.dropdown-menu button:hover {
  background: #f0f0f0;
}

.ticket-list {
  flex: 1;
  padding: 10px;
  box-sizing: border-box;
  overflow: auto;
}

.ticket-list h3 {
  margin-top: 0;
  margin-bottom: 12px;
  font-size: 1.1rem;
  text-align: left;
}

.ticket-list table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
  table-layout: fixed;
  font-family: 'Montserrat', sans-serif;
}

.ticket-list th, .ticket-list td {
  border-bottom: 1px solid #e0e0e0;
  padding: 6px 4px;
  text-align: left;
  word-wrap: break-word;
  vertical-align: middle;
}

/* Estilos para la celda de fecha y hora */
.fecha-hora-cell {
  padding: 8px 4px !important;
  text-align: center;
  vertical-align: middle;
}

.fecha-line {
  font-size: 0.85rem;
  font-weight: 500;
  color: #495057;
  line-height: 1.2;
  margin-bottom: 2px;
}

.hora-line {
  font-size: 0.75rem;
  color: #6c757d;
  font-weight: 400;
  line-height: 1.2;
}

/* Optimización de ancho de columnas */
.ticket-list th:nth-child(1), .ticket-list td:nth-child(1) { /* Folio (usuarios) */
  width: 120px;
  min-width: 120px;
  max-width: 120px;
  text-align: center;
  padding: 8px 6px;
}

.ticket-list th:nth-child(2), .ticket-list td:nth-child(2) { /* Título */
  width: 220px;
  min-width: 180px;
}

.ticket-list th:nth-child(3), .ticket-list td:nth-child(3) { /* Solicitante (solo admin) */
  width: 120px;
  min-width: 100px;
}

.ticket-list th:nth-child(4), .ticket-list td:nth-child(4) { /* Área Solicitante */
  width: 120px;
  min-width: 100px;
}

.ticket-list th:nth-child(5), .ticket-list td:nth-child(5) { /* Prioridad */
  width: 100px;
  min-width: 80px;
}

.ticket-list th:nth-child(6), .ticket-list td:nth-child(6) { /* Fecha Creación */
  width: 90px;
  min-width: 80px;
  text-align: center;
  font-size: 0.8rem;
}

.ticket-list th:nth-child(7), .ticket-list td:nth-child(7) { /* Fecha Cierre */
  width: 90px;
  min-width: 80px;
  text-align: center;
  font-size: 0.8rem;
}

.ticket-list th:nth-child(8), .ticket-list td:nth-child(8) { /* Técnico */
  width: 120px;
  min-width: 100px;
}

.ticket-list th:nth-child(9), .ticket-list td:nth-child(9) { /* Estado */
  width: 100px;
  min-width: 80px;
  text-align: center;
}


.ticket-list th {
  background: #f0f0f0;
  font-weight: 600;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.85rem;
  color: #495057;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.ticket-list tr:last-child td {
  border-bottom: none;
}

/* Estilos para filas clickeables */
.ticket-row {
  cursor: pointer;
  transition: all 0.3s ease;
}

.ticket-row:hover {
  background: #f8fff9;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 154, 68, 0.1);
}

.ticket-row:active {
  transform: translateY(0);
  box-shadow: 0 1px 4px rgba(0, 154, 68, 0.2);
}

/* Estilos para badges de estado */
.estado-badge {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-align: center;
  display: inline-block;
  min-width: 80px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-pending {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.badge-in-progress {
  background: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
}

.badge-resolved {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.badge-reassigned {
  background: #ffeaa7;
  color: #856404;
  border: 1px solid #fdcb6e;
}

.badge-cancelled {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.badge-cancel {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.badge-reassign {
  background: #e2e3e5;
  color: #383d41;
  border: 1px solid #d6d8db;
}

.badge-closed {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}



/* Estilos para el filtro de áreas */
.filter-section {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #e9ecef;
}

.filter-controls {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.filter-controls label {
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
  margin: 0;
}

.area-filter {
  padding: 8px 12px;
  border: 2px solid #e9ecef;
  border-radius: 6px;
  font-size: 0.9rem;
  background: white;
  color: #495057;
  min-width: 200px;
  transition: all 0.3s ease;
}

.area-filter:focus {
  outline: none;
  border-color: #009a44;
  box-shadow: 0 0 0 3px rgba(0, 154, 68, 0.1);
}

/* Estilos para autocomplete de áreas */
.autocomplete-container {
  min-width: 250px;
  max-width: 350px;
}

.area-autocomplete {
  font-size: 0.9rem !important;
}

.area-autocomplete >>> .v-input__control {
  min-height: 45px !important;
}

.area-autocomplete >>> .v-text-field__details {
  display: none !important;
}

.clear-filter-btn {
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;
  align-self: flex-end;
}

.clear-filter-btn:hover {
  background: #c82333;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(220, 53, 69, 0.3);
}

.filter-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.85rem;
  color: #6c757d;
}

.ticket-count {
  font-weight: 600;
  color: #009a44;
  background: #d4edda;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
}

/* Responsive para el filtro */
@media (max-width: 768px) {
  .filter-controls {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
  
  .filter-group {
    width: 100%;
  }
  
  .area-filter {
    min-width: auto;
    width: 100%;
  }
  
  .clear-filter-btn {
    align-self: stretch;
    justify-content: center;
  }
  
  .filter-info {
    flex-direction: column;
    gap: 5px;
    text-align: center;
  }
}

.form-group {
  margin-bottom: 12px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
}

.required {
  color: #ef4444;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.95rem;
  box-sizing: border-box;
}

.form-group input[readonly] {
  background-color: #f8f9fa;
  color: #6c757d;
  cursor: not-allowed;
  border-color: #dee2e6;
}

.submit-btn {
  width: 100%;
  padding: 10px;
  background: #009a44;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  opacity: 1;
  transition: background 0.2s;
}

.submit-btn:active {
  background: #007a36;
}

.detalle-btn {
  background: #009a44;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 3px 8px;
  font-size: 0.8rem;
  cursor: pointer;
  margin: 0 1px;
  transition: background 0.2s;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
}

.detalle-btn:active {
  background: #007a36;
}

.inline-select {
  padding: 4px 6px;
  font-size: 0.85rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: white;
  font-family: 'Montserrat', sans-serif;
  width: 90px;
  min-width: 90px;
}

.inline-input {
  padding: 4px 6px;
  font-size: 0.85rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 90px;
  min-width: 90px;
  font-family: 'Montserrat', sans-serif;
}

.delete-btn {
  background: #dc2626;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 3px 6px;
  font-size: 0.75rem;
  cursor: pointer;
  margin-left: 2px;
  transition: background 0.2s;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
}

.delete-btn:hover {
  background: #b91c1c;
}

.delete-btn:active {
  background: #991b1b;
}



.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #fff;
  padding: 20px;
  border-radius: 12px;
  min-width: 400px;
  max-width: 95vw;
  max-height: 90vh;
  box-shadow: 0 4px 20px rgba(0,0,0,0.25);
  overflow-y: auto;
  box-sizing: border-box;
}


.evidencia-placeholder {
  color: #888;
  font-size: 0.95rem;
  margin-left: 8px;
}

.modal-actions {
  text-align: right;
}

.modal-actions button {
  background: #009a44;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 6px 16px;
  font-size: 1rem;
  cursor: pointer;
  margin-top: 8px;
}

.modal-actions button:active {
  background: #007a36;
}

/* Estilos para el modal de reasignación */
.reassign-modal {
  max-width: 500px;
  width: 90%;
}

.reassign-info {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  border-left: 4px solid #009a44;
}

.reassign-info p {
  margin: 5px 0;
  font-size: 0.9rem;
}

.reassign-info strong {
  color: #009a44;
}

/* Estilos para el modal de cancelación */
.cancel-modal {
  max-width: 500px;
  width: 90%;
}

.cancel-info {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  border-left: 4px solid #dc3545;
}

.cancel-info p {
  margin: 5px 0;
  font-size: 0.9rem;
}

.cancel-info strong {
  color: #dc3545;
}

.warning-message {
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 8px;
  padding: 15px;
  margin-top: 15px;
}

.warning-message p {
  margin: 0;
  color: #856404;
  font-size: 0.9rem;
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.btn-danger:hover {
  background: #c82333;
}

.modal-footer {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #e9ecef;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
}

.btn-primary {
  background: #009a44;
  color: white;
}

.btn-primary:hover {
  background: #007a36;
}

/* Estilos para botón de cancelar ticket */
.cancel-ticket-btn {
  background: #dc3545;
  color: white;
  border: none;
  padding: 4px 8px;
  border-radius: 3px;
  font-size: 0.7rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.cancel-ticket-btn:hover {
  background: #c82333;
  transform: translateY(-1px);
}

.cancel-ticket-btn:active {
  background: #bd2130;
  transform: translateY(0);
}

/* Estilos para botón de cancelar en el detalle del ticket */
.cancel-ticket-btn-detail {
  background: linear-gradient(135deg, #dc3545, #c82333);
  color: white;
  border: none;
  padding: 10px 18px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
}

.cancel-ticket-btn-detail:hover {
  background: linear-gradient(135deg, #c82333, #bd2130);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.4);
}

.cancel-ticket-btn-detail:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(220, 53, 69, 0.3);
}

.no-action-text {
  color: #6c757d;
  font-style: italic;
  font-size: 0.9rem;
}

/* Estilo para la columna de acciones */
td:last-child {
  width: 80px;
  min-width: 80px;
  max-width: 80px;
  text-align: center;
  padding: 4px;
}

/* Estilo para el encabezado de Folio (usuarios) */
.folio-header {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #009a44;
  width: 120px !important;
  min-width: 120px !important;
  max-width: 120px !important;
  padding: 8px 6px !important;
  text-align: center !important;
  word-wrap: normal;
  overflow: hidden;
}

/* Estilo para la celda de folio (usuarios) */
.folio-cell {
  font-family: 'Courier New', monospace;
  font-weight: 700;
  font-size: 0.95rem;
  color: #009a44;
  letter-spacing: 0.5px;
  width: 120px !important;
  min-width: 120px !important;
  max-width: 120px !important;
  padding: 8px 6px !important;
  text-align: center !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Estilo específico para columna ID de admin (más pequeña) */
.admin-view .ticket-list th:nth-child(1),
.admin-view .ticket-list td:nth-child(1) {
  width: 60px !important;
  min-width: 60px !important;
  max-width: 60px !important;
  padding: 6px 4px !important;
  font-size: 0.85rem !important;
}

/* Asegurar que en vista de usuario el folio mantenga 120px */
.ticket-form-container:not(.admin-view) .ticket-list th:nth-child(1),
.ticket-form-container:not(.admin-view) .ticket-list td:nth-child(1) {
  width: 120px !important;
  min-width: 120px !important;
  max-width: 120px !important;
  padding: 8px 6px !important;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: #f8f9fa;
  color: #495057;
}

/* Estilos para el modal del formulario */
.form-modal {
  max-width: 900px;
  width: 98%;
  max-height: 95vh;
  overflow-y: auto;
  margin: 10px;
  box-sizing: border-box;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 12px;
}

.modal-header h3 {
  margin: 0;
  color: #009a44;
  font-size: 1.1rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  color: #6c757d;
  padding: 3px;
  border-radius: 4px;
  transition: background 0.2s;
}

.close-btn:hover {
  background: #f8f9fa;
}

.ticket-form-modal {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ticket-form-modal .form-group {
  margin-bottom: 0;
}

.ticket-form-modal .form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

/* Estilos para las secciones del formulario */
.form-section {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 12px;
  border-left: 3px solid #009a44;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.section-title {
  margin: 0 0 10px 0;
  color: #009a44;
  font-size: 0.95rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 5px;
}

.form-section .form-group label {
  color: #495057;
  font-weight: 600;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 0.85rem;
}

.form-section .form-group input,
.form-section .form-group textarea,
.form-section .form-group select {
  border: 1px solid #e9ecef;
  border-radius: 5px;
  padding: 7px 10px;
  font-size: 0.85rem;
  transition: all 0.3s ease;
  background: white;
}

.form-section .form-group input:focus,
.form-section .form-group textarea:focus,
.form-section .form-group select:focus {
  border-color: #009a44;
  box-shadow: 0 0 0 2px rgba(0, 154, 68, 0.1);
  outline: none;
}

.form-section .form-group input:hover,
.form-section .form-group textarea:hover,
.form-section .form-group select:hover {
  border-color: #009a44;
}

.ticket-form-modal .modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 12px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 6px;
  border-top: 2px solid #009a44;
}

.cancel-btn {
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 5px;
  padding: 8px 18px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;
}

.cancel-btn:hover {
  background: #5a6268;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(108, 117, 125, 0.3);
}

.ticket-form-modal .submit-btn {
  width: auto;
  padding: 8px 22px;
  background: #009a44;
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;
}

.ticket-form-modal .submit-btn:hover {
  background: #007a36;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 154, 68, 0.3);
}

.ticket-form-modal .submit-btn:disabled {
  background: #6c757d;
  color: #fff;
  cursor: not-allowed;
  opacity: 0.7;
  transform: none;
  box-shadow: none;
}

.ticket-form-modal .submit-btn:disabled:hover {
  background: #6c757d;
  transform: none;
  box-shadow: none;
}

/* Estilos para radio buttons */
.radio-group {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 8px;
  margin-top: 8px;
}

.radio-item {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 0.85rem;
  padding: 8px 12px;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  transition: all 0.3s ease;
  position: relative;
}

.radio-item:hover {
  border-color: #009a44;
  background: #f8fff9;
  transform: translateY(-1px);
  box-shadow: 0 1px 4px rgba(0, 154, 68, 0.1);
}

.radio-item input[type="radio"] {
  width: 16px;
  height: 16px;
  margin-right: 8px;
  cursor: pointer;
  accent-color: #009a44;
}

.radio-item input[type="radio"]:checked + span {
  color: #009a44;
  font-weight: 600;
}

.radio-item:has(input[type="radio"]:checked) {
  border-color: #009a44;
  background: #f0fff4;
}

/* Estilos para checkbox personalizado */
.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0;
}

.checkbox-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  margin-right: 6px;
  cursor: pointer;
  accent-color: #009a44;
}

.checkbox-custom {
  display: none;
}

/* Estilos para el formulario de equipo */
.equipo-form {
  background: #f0fff4;
  border: 1px solid #009a44;
  border-radius: 5px;
  padding: 6px;
  margin-top: 4px;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.equipo-form .form-group {
  margin-bottom: 8px;
}

.equipo-form .form-group:last-child {
  margin-bottom: 0;
}

/* Estilos para el formulario de insumos */
.insumos-form {
  background: #fff3e0;
  border: 1px solid #ffcc02;
  border-radius: 5px;
  padding: 6px;
  margin-top: 4px;
  animation: slideDown 0.3s ease-out;
}

.insumos-form .form-group {
  margin-bottom: 8px;
}

.insumos-form .form-group:last-child {
  margin-bottom: 0;
}

/* Responsive para radio buttons */
@media (max-width: 768px) {
  .radio-group {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  
  .radio-item {
    font-size: 0.9rem;
    padding: 10px 12px;
  }
  
  .ticket-form-modal .form-row {
    grid-template-columns: 1fr;
  }
  
  .form-section {
    padding: 15px;
  }
  
  .section-title {
    font-size: 1rem;
  }
  
  .ticket-form-modal .modal-actions {
    flex-direction: column;
    gap: 10px;
  }
  
  .cancel-btn,
  .ticket-form-modal .submit-btn {
    width: 100%;
    justify-content: center;
  }
}

/* Estilos para modales de estadísticas */
.stats-modal {
  max-width: 900px;
  width: 95%;
  max-height: 90vh;
  overflow-y: auto;
}

/* Mejoras para modales en dispositivos móviles */
@media (max-width: 768px) {
  .stats-modal {
    width: 100%;
    max-width: 100vw;
    max-height: 100vh;
    margin: 0;
    border-radius: 0;
  }
  
  .stats-modal .modal-content {
    max-width: 100vw;
    max-height: 100vh;
    padding: 15px 10px;
    border-radius: 0;
  }
}

@media (max-width: 480px) {
  .stats-modal {
    width: 100vw;
    height: 100vh;
    margin: 0;
    border-radius: 0;
  }
  
  .stats-modal .modal-content {
    max-width: 100vw;
    max-height: 100vh;
    padding: 10px 8px;
    border-radius: 0;
  }
}

.stats-content {
  padding: 20px 0;
}

/* Estilos para reportes por técnico */
.stats-summary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 25px;
}

.stat-card {
  background: linear-gradient(135deg, #009a44, #007a36);
  color: white;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 154, 68, 0.3);
}

.stat-number {
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 0.9rem;
  opacity: 0.9;
}

.stats-table {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.stats-table table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.stats-table th {
  background: #f8f9fa;
  padding: 12px;
  font-weight: 600;
  color: #495057;
  text-align: left;
  border-bottom: 2px solid #e9ecef;
}

.stats-table td {
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
}

.efficiency {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

.efficiency.high {
  background: #d4edda;
  color: #155724;
}

.efficiency.medium {
  background: #fff3cd;
  color: #856404;
}

.efficiency.low {
  background: #f8d7da;
  color: #721c24;
}

/* Estilos para exportar reportes */
.export-options {
  display: grid;
  gap: 20px;
}

.export-option {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 10px;
  border: 2px solid #e9ecef;
  transition: all 0.3s ease;
}

.export-option:hover {
  border-color: #009a44;
  background: #f0fff4;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 154, 68, 0.1);
}

.export-icon {
  font-size: 2.5rem;
  min-width: 60px;
  text-align: center;
}

.export-info {
  flex: 1;
}

.export-info h4 {
  margin: 0 0 8px 0;
  color: #009a44;
  font-size: 1.1rem;
}

.export-info p {
  margin: 0 0 12px 0;
  color: #6c757d;
  font-size: 0.9rem;
}

.export-btn {
  background: #009a44;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.export-btn:hover {
  background: #007a36;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 154, 68, 0.3);
}

/* Estilos para gráficos por departamento */
.chart-container {
  margin-bottom: 25px;
}

.chart-item {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 8px;
}

.chart-label {
  min-width: 120px;
  font-weight: 600;
  color: #495057;
}

.chart-bar {
  flex: 1;
  height: 20px;
  background: #e9ecef;
  border-radius: 10px;
  overflow: hidden;
}

.chart-fill {
  height: 100%;
  background: linear-gradient(90deg, #009a44, #007a36);
  border-radius: 10px;
  transition: width 0.8s ease;
}

.chart-percentage {
  min-width: 50px;
  text-align: right;
  font-weight: 600;
  color: #009a44;
}

.chart-summary {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e9ecef;
}

.summary-item:last-child {
  border-bottom: none;
}

.summary-label {
  color: #6c757d;
  font-size: 0.9rem;
}

.summary-value {
  font-weight: 600;
  color: #009a44;
}

/* Estilos para tiempo promedio */
.time-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 25px;
}

.time-card {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  transition: transform 0.3s ease;
}

.time-card:hover {
  transform: translateY(-3px);
}

.time-card.critical {
  background: linear-gradient(135deg, #dc3545, #c82333);
  color: white;
}

.time-card.high {
  background: linear-gradient(135deg, #fd7e14, #e55a00);
  color: white;
}

.time-card.medium {
  background: linear-gradient(135deg, #ffc107, #e0a800);
  color: #212529;
}

.time-card.low {
  background: linear-gradient(135deg, #6c757d, #545b62);
  color: white;
}

.time-icon {
  font-size: 2rem;
  min-width: 50px;
  text-align: center;
}

.time-info {
  flex: 1;
}

.time-label {
  font-size: 0.9rem;
  opacity: 0.9;
  margin-bottom: 5px;
}

.time-value {
  font-size: 1.3rem;
  font-weight: bold;
  margin-bottom: 3px;
}

.time-tickets {
  font-size: 0.8rem;
  opacity: 0.8;
}

.time-summary {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

/* Responsive para estadísticas */
@media (max-width: 768px) {
  .stats-summary {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .export-option {
    flex-direction: column;
    text-align: center;
    gap: 15px;
  }
  
  .time-stats {
    grid-template-columns: 1fr;
  }
  
  .chart-item {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
  
  .chart-label {
    min-width: auto;
  }
  
  .chart-percentage {
    text-align: left;
  }
}

@media (max-width: 1400px) {
  .main-content {
    width: 98%;
    max-width: 1200px;
  }
  .form-row {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 1200px) {
  .main-content {
    width: 98%;
    max-width: 1000px;
  }
  .form-row {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 768px) {
  .main-content {
    width: 98%;
    max-width: 600px;
  }
  .form-row {
    grid-template-columns: 1fr;
  }
  .table-header {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }
  .header-actions {
    justify-content: center;
  }
  .dropdown-menu {
    right: auto;
    left: 0;
  }
  .ticket-form-modal .form-row {
    grid-template-columns: 1fr;
  }
  
  /* Ajustes para la tabla en móviles */
  table {
    font-size: 0.8rem;
  }
  
  table th,
  table td {
    padding: 6px 4px;
  }
  
  /* Ajustes específicos para el modal en móviles */
  .form-modal {
    width: 98%;
    max-width: 98vw;
    max-height: 98vh;
    margin: 5px;
  }
  
  .modal-content {
    max-width: 98vw;
    max-height: 95vh;
    padding: 15px 12px;
  }
  
  .ticket-form-modal {
    gap: 6px;
  }
  
  .form-group {
    margin-bottom: 10px;
  }
  
  .form-group input,
  .form-group select,
  .form-group textarea {
    font-size: 16px; /* Evita zoom en iOS */
    padding: 8px 6px;
  }
}

@media (max-width: 480px) {
  .main-content {
    width: 100%;
    margin: 5px;
  }
  .ticket-form-container {
    padding: 5px 0;
  }
  .table-header {
    padding: 10px;
  }
  .ticket-list {
    padding: 10px;
  }
  
  /* Ajustes para pantallas muy pequeñas */
  .form-modal {
    width: 100%;
    max-width: 100vw;
    max-height: 100vh;
    margin: 0;
    border-radius: 0;
  }
  
  .modal-content {
    max-width: 100vw;
    max-height: 100vh;
    padding: 12px 10px;
    border-radius: 0;
  }
  
  .modal-header {
    padding: 8px 0;
    margin-bottom: 8px;
  }
  
  .modal-header h3 {
    font-size: 1rem;
  }
  
  .ticket-form-modal {
    gap: 5px;
  }
  
  .form-group {
    margin-bottom: 6px;
  }
  
  .form-group label {
    font-size: 0.8rem;
    margin-bottom: 3px;
  }
  
  .form-group input,
  .form-group select,
  .form-group textarea {
    font-size: 16px;
    padding: 6px 4px;
  }
  
  .equipo-form,
  .insumos-form {
    padding: 4px;
    margin-top: 3px;
  }
  
  /* Ajustes adicionales para la tabla en pantallas muy pequeñas */
  table {
    font-size: 0.7rem;
  }
  
  table th,
  table td {
    padding: 3px 1px;
  }
  
  .ticket-list {
    overflow-x: auto;
  }
  
  .filter-controls {
    flex-direction: column;
    gap: 6px;
  }
  
  .filter-group {
    width: 100%;
  }
  
  .area-filter {
    width: 100%;
  }
}

/* Animaciones para notificaciones */
@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideOutRight {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
}


/* Estilos para el contador de cantidad */
.quantity-counter {
  display: flex;
  align-items: center;
  border: 1px solid #ccc;
  border-radius: 4px;
  overflow: hidden;
  background: white;
}

.counter-btn {
  background: #009a44;
  color: white;
  border: none;
  padding: 8px 12px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.counter-btn:hover {
  background: #007a35;
  transform: scale(1.05);
}

.counter-btn:active {
  transform: scale(0.95);
}

.counter-input {
  flex: 1;
  border: none;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  padding: 8px;
  background: white;
  color: #333;
  min-width: 60px;
}

.counter-input:focus {
  outline: none;
  background: #f8f9fa;
}

.counter-input::-webkit-inner-spin-button,
.counter-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  appearance: none;
  margin: 0;
}

.counter-input[type=number] {
  -moz-appearance: textfield;
  appearance: textfield;
}

/* Estilos para autocompletado de insumos */
.autocomplete-container {
  position: relative;
  width: 100%;
}

.sugerencias-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ccc;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.sugerencia-item {
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  font-size: 13px;
  transition: background-color 0.2s ease;
  line-height: 1.3;
}

.sugerencia-descripcion {
  font-weight: 600;
  color: #333;
  margin-bottom: 2px;
}

.sugerencia-presentacion {
  font-size: 11px;
  color: #666;
  font-style: italic;
}

.sugerencia-item:hover {
  background-color: #009a44;
}

.sugerencia-item:hover .sugerencia-descripcion {
  color: white;
}

.sugerencia-item:hover .sugerencia-presentacion {
  color: #e0e0e0;
}

.sugerencia-item:last-child {
  border-bottom: none;
}

.sugerencia-item:active {
  background-color: #007a35;
}

/* Estilos para prioridad automática */
.prioridad-badge {
  font-weight: bold;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.prioridad-display {
  font-weight: bold;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.tiempo-respuesta {
  color: #666;
  font-style: italic;
  font-size: 0.9rem;
}

.justificacion-prioridad {
  color: #333;
  font-size: 0.9rem;
  line-height: 1.4;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #009a44;
}

/* Estilos para filas de tickets con prioridad */
.ticket-row {
  transition: all 0.3s ease;
}

.ticket-row:hover {
  transform: translateX(2px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

/* Estilos para el panel de detalle del ticket */
.detail-header {
  display: flex;
  align-items: center;
  gap: 15px;
}

.back-btn {
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;
}

.back-btn:hover {
  background: #5a6268;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(108, 117, 125, 0.3);
}

.ticket-detail-panel {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  background: #f8f9fa;
}

.detail-content {
  max-width: 1200px;
  margin: 0 auto;
}



/* Indicador de prioridad manual */
.manual-indicator {
  margin-left: 4px;
  font-size: 0.8rem;
  opacity: 0.8;
  cursor: help;
}

.single-detail-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  overflow: hidden;
  border: 1px solid #e9ecef;
}

.card-header {
  background: linear-gradient(135deg, #009a44, #007a36);
  color: white;
  padding: 25px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.header-left {
  flex: 1;
}

.header-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.download-btn {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.download-btn:hover {
  background: linear-gradient(135deg, #218838 0%, #1ea085 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
}

/* Estilos para botones de administrador */
.admin-download-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
}

.download-btn.normal-report {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
}

.download-btn.normal-report:hover {
  background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
  box-shadow: 0 4px 15px rgba(0, 123, 255, 0.3);
}

.download-btn.complete-report {
  background: linear-gradient(135deg, #6f42c1 0%, #5a2d91 100%);
}

.download-btn.complete-report:hover {
  background: linear-gradient(135deg, #5a2d91 0%, #4c2474 100%);
  box-shadow: 0 4px 15px rgba(111, 66, 193, 0.3);
}

.close-detail-btn {
  background: #6c757d;
  color: white;
  border: none;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
}

.close-detail-btn:hover {
  background: #5a6268;
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(108, 117, 125, 0.3);
}

.card-header h2 {
  font-family: 'Helvetica LT Std Black Condensed Oblique', 'Helvetica', Arial, sans-serif;
  font-size: 1.5rem;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
}

.status-summary {
  display: flex;
  gap: 10px;
  align-items: center;
}

.card-content {
  padding: 30px;
}

/* Nueva estructura en secciones organizadas */
.detail-section {
  margin-bottom: 30px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.section-header {
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  padding: 15px 20px;
  border-bottom: 1px solid #dee2e6;
}

.section-header h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-content {
  padding: 20px;
}

.status-badges-row {
  display: flex;
  gap: 30px;
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.status-badge-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  text-align: center;
}

.status-badge-item label {
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
  margin: 0;
}

.detail-form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.detail-form-group {
  margin-bottom: 15px;
}

.detail-form-group.full-width {
  grid-column: 1 / -1;
}

.detail-form-group label {
  display: block;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
  font-size: 0.9rem;
}

.detail-value {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 12px;
  font-size: 0.95rem;
  min-height: 20px;
  word-wrap: break-word;
  color: #333;
  transition: all 0.3s ease;
}

.detail-value:hover {
  border-color: #009a44;
  background: #f0fff4;
}

.detail-value.empty {
  color: #6c757d;
  font-style: italic;
}

.detail-value.title-value {
  font-size: 1.1rem;
  font-weight: 600;
  color: #009a44;
  background: #f0fff4;
  border-color: #009a44;
}

.detail-value.description-value {
  min-height: 60px;
  line-height: 1.5;
}

.detail-sub-value {
  margin-top: 8px;
  padding: 6px 10px;
  background: #e9ecef;
  border-radius: 4px;
  font-size: 0.85rem;
  color: #6c757d;
}

.detail-sub-value small {
  display: block;
  line-height: 1.4;
}

/* Estilos para la sección de solución del técnico */
.solution-section {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 2px solid #009a44;
  border-radius: 12px;
  padding: 25px;
  margin: 20px 0;
  box-shadow: 0 4px 12px rgba(0, 154, 68, 0.1);
}

.solution-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #009a44;
}

.solution-header h3 {
  font-family: 'Helvetica LT Std Black Condensed Oblique', 'Helvetica', Arial, sans-serif;
  color: #009a44;
  font-size: 1.2rem;
  margin: 0;
}

.solution-badge {
  background: #009a44;
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Estilos para la sección de solución visible para usuarios */
.solution-display-section {
  background: linear-gradient(135deg, #e8f5e8 0%, #f0f8f0 100%);
  border: 2px solid #28a745;
  border-radius: 12px;
  padding: 25px;
  margin: 20px 0;
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.1);
}

.solution-display-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #28a745;
}

.solution-display-header h3 {
  font-family: 'Helvetica LT Std Black Condensed Oblique', 'Helvetica', Arial, sans-serif;
  color: #28a745;
  font-size: 1.2rem;
  margin: 0;
}

.solution-status-badge {
  background: #28a745;
  color: white;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: bold;
}

.solution-display-content {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.solution-text {
  background: white;
  border: 1px solid #d4edda;
  border-radius: 8px;
  padding: 15px;
  font-size: 0.95rem;
  line-height: 1.6;
  color: #2c3e50;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.detail-textarea {
  width: 100%;
  padding: 12px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: 'Helvetica LT Std Condensed Oblique', 'Helvetica', Arial, sans-serif;
  background: white;
  color: #333;
  transition: all 0.3s ease;
  resize: vertical;
  min-height: 80px;
}

.detail-textarea:focus {
  outline: none;
  border-color: #009a44;
  box-shadow: 0 0 0 3px rgba(0, 154, 68, 0.1);
  background: #f8fff9;
}

.detail-textarea::placeholder {
  color: #6c757d;
  font-style: italic;
}

.detail-input {
  width: 100%;
  padding: 12px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: 'Helvetica LT Std Condensed Oblique', 'Helvetica', Arial, sans-serif;
  background: white;
  color: #333;
  transition: all 0.3s ease;
}

.detail-input:focus {
  outline: none;
  border-color: #009a44;
  box-shadow: 0 0 0 3px rgba(0, 154, 68, 0.1);
  background: #f8fff9;
}

.detail-input::placeholder {
  color: #6c757d;
  font-style: italic;
}

.solution-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #dee2e6;
  flex-wrap: wrap;
  gap: 15px;
}

.save-solution-btn {
  background: linear-gradient(135deg, #009a44, #007a36);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 2px 8px rgba(0, 154, 68, 0.3);
}

.save-solution-btn:hover {
  background: linear-gradient(135deg, #007a36, #006a2e);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 154, 68, 0.4);
}

.save-solution-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(0, 154, 68, 0.3);
}

.last-updated {
  color: #6c757d;
  font-size: 0.85rem;
  font-style: italic;
}

.status-badges {
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
}

.detail-badge {
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.85rem;
  text-align: center;
  min-width: 80px;
}

.detail-badge-prioridad {
  color: white;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
}

.detail-badge-estado {
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.85rem;
}

.detail-badge-estado.badge-pendiente {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.detail-badge-estado.badge-in-progress {
  background: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
}

.detail-badge-estado.badge-resolved {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.detail-badge-estado.badge-reassigned {
  background: #ffeaa7;
  color: #856404;
  border: 1px solid #fdcb6e;
}

.detail-badge-estado.badge-cancelled {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.detail-badge-estado.badge-cancel {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

/* Responsive para el panel de detalle */
@media (max-width: 768px) {
  .detail-section {
    margin-bottom: 20px;
  }
  
  .section-header {
    padding: 12px 15px;
  }
  
  .section-content {
    padding: 15px;
  }
  
  .status-badges-row {
    flex-direction: column;
    gap: 15px;
    padding: 12px;
  }
  
  .detail-form-row {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .detail-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .back-btn {
    align-self: flex-start;
  }
  
  .ticket-detail-panel {
    padding: 15px;
  }
  
  .card-content {
    padding: 20px;
  }
  
  .card-header {
    padding: 20px;
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .card-header h2 {
    font-size: 1.3rem;
  }
  
  .status-summary {
    align-self: stretch;
    justify-content: center;
  }
  
  .solution-section {
    padding: 20px;
  }
  
  .solution-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .solution-actions {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
  
  .save-solution-btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .card-content {
    padding: 15px;
  }
  
  .card-header {
    padding: 15px;
  }
  
  .detail-form-row {
    gap: 10px;
  }
  
  .detail-value {
    padding: 10px;
  }
  
  .solution-section {
    padding: 15px;
  }
  
  .detail-textarea,
  .detail-input {
    padding: 10px;
  }
}

/* Estilos para adjuntar fotos */
.photo-upload-section {
  margin-bottom: 15px;
}

.photo-input {
  display: none;
}

.photo-upload-btn {
  display: inline-block;
  background: linear-gradient(135deg, #009a44, #007a36);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  border: none;
  font-size: 0.9rem;
  text-align: center;
  min-width: 150px;
}

.photo-upload-btn:hover {
  background: linear-gradient(135deg, #007a36, #006a2e);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 154, 68, 0.4);
}

.photo-upload-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(0, 154, 68, 0.3);
}

.photo-help-text {
  margin: 8px 0 0 0;
  color: #6c757d;
  font-size: 0.85rem;
  font-style: italic;
}

.photos-preview {
  margin-top: 15px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.photos-preview h4 {
  margin: 0 0 15px 0;
  color: #009a44;
  font-size: 1rem;
  font-weight: 600;
}

.photos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
}

.photo-item {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
}

.photo-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.photo-preview {
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-bottom: 1px solid #e9ecef;
}

.photo-info {
  padding: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
}

.photo-name {
  font-size: 0.85rem;
  color: #495057;
  font-weight: 500;
  flex: 1;
  margin-right: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.remove-photo-btn {
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.remove-photo-btn:hover {
  background: #c82333;
  transform: scale(1.1);
}

.remove-photo-btn:active {
  transform: scale(0.95);
}

/* Responsive para fotos */
@media (max-width: 768px) {
  .photos-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 10px;
  }
  
  .photo-preview {
    height: 120px;
  }
  
  .photo-info {
    padding: 8px;
  }
  
  .photo-name {
    font-size: 0.8rem;
  }
  
  .photo-upload-btn {
    width: 100%;
    padding: 10px;
  }
}

@media (max-width: 480px) {
  .photos-grid {
    grid-template-columns: 1fr;
  }
  
  .photo-preview {
    height: 200px;
  }
  
  .header-actions {
    width: 100%;
    justify-content: space-between;
  }
  
  .download-btn {
    padding: 8px 12px;
    font-size: 0.8rem;
  }
  
  .admin-download-buttons {
    flex-direction: column;
    gap: 6px;
    width: 100%;
  }
  
  .admin-download-buttons .download-btn {
    width: 100%;
    justify-content: center;
  }
  
  .close-detail-btn {
    width: 35px;
    height: 35px;
    font-size: 0.9rem;
  }
}

/* ===== ESTILOS PARA SECCIÓN DE SOLUCIÓN DEL TÉCNICO ===== */

.solution-section {
  margin-top: 20px;
  background: #ffffff;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #dee2e6;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.solution-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #f8f9fa;
}

.solution-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.2rem;
  font-weight: 600;
}

.solution-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.detail-form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-form-group.full-width {
  grid-column: 1 / -1;
}

.detail-form-group label {
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
}

.detail-textarea {
  width: 100%;
  padding: 12px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.9rem;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.3s ease;
}

.detail-textarea:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.detail-input {
  width: 100%;
  padding: 12px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: border-color 0.3s ease;
}

.detail-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.solution-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  padding-top: 15px;
  border-top: 1px solid #e9ecef;
}

.save-solution-btn {
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
}

.save-solution-btn:hover {
  background: linear-gradient(135deg, #20c997, #17a2b8);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
}

.last-updated {
  color: #6c757d;
  font-size: 0.8rem;
  font-style: italic;
}

/* ===== ESTILOS PARA FEED DE SOLUCIONES (ESTILO FACEBOOK) ===== */

.solutions-feed-section {
  margin-top: 20px;
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e9ecef;
}

.feed-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #dee2e6;
}

.feed-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.2rem;
  font-weight: 600;
}

.feed-count {
  background: #007bff;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
}

.solutions-feed {
  max-height: 400px;
  overflow-y: auto;
}

.feed-item {
  display: flex;
  margin-bottom: 16px;
  padding: 12px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
  position: relative;
}

.feed-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.feed-item:last-child {
  margin-bottom: 0;
}

.feed-avatar {
  margin-right: 12px;
  flex-shrink: 0;
}

.avatar-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: white;
}

.feed-content {
  flex: 1;
  min-width: 0;
}

.feed-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  gap: 8px;
}

.feed-author {
  color: #2c3e50;
  font-weight: 600;
  font-size: 0.9rem;
}

.feed-type-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-solucion_inicial {
  background: #28a745;
  color: white;
}

.badge-actualizacion {
  background: #17a2b8;
  color: white;
}

.badge-seguimiento {
  background: #ffc107;
  color: #212529;
}

.badge-cierre {
  background: #6c757d;
  color: white;
}

.feed-time {
  color: #6c757d;
  font-size: 0.8rem;
  margin-left: auto;
}

.feed-message {
  color: #495057;
  line-height: 1.5;
  font-size: 0.9rem;
  white-space: pre-wrap;
  word-break: break-word;
}

.feed-action {
  margin-top: 4px;
  font-size: 0.75rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.feed-extra-info {
  margin-top: 6px;
  font-size: 0.8rem;
  color: #495057;
}

/* Tipos de feed con colores específicos */
.feed-type-solucion_inicial {
  border-left: 4px solid #28a745;
}

.feed-type-actualizacion {
  border-left: 4px solid #17a2b8;
}

.feed-type-seguimiento {
  border-left: 4px solid #ffc107;
}

.feed-type-cierre {
  border-left: 4px solid #6c757d;
}

/* Formulario para nueva solución */
.new-solution-section {
  margin-top: 20px;
  background: #ffffff;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #dee2e6;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.new-solution-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #f8f9fa;
}

.new-solution-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.2rem;
  font-weight: 600;
}

.solution-status {
  background: #e9ecef;
  color: #6c757d;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
}

.new-solution-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
}

.form-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.add-solution-btn {
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
}

.add-solution-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #0056b3, #004085);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.add-solution-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
  opacity: 0.6;
}

.clear-btn {
  background: #6c757d;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 6px;
}

.clear-btn:hover {
  background: #5a6268;
  transform: translateY(-1px);
}

/* Estilos para carga de imágenes */
.image-upload-section {
  margin-top: 10px;
}

.image-input {
  display: none;
}

.image-upload-btn {
  background: #28a745;
  color: white;
  padding: 10px 15px;
  border-radius: 6px;
  cursor: pointer;
  display: inline-block;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
}

.image-upload-btn:hover {
  background: #218838;
  transform: translateY(-1px);
}

.image-help-text {
  font-size: 0.8rem;
  color: #6c757d;
  margin-top: 5px;
  font-style: italic;
}

/* Vista previa de imágenes */
.images-preview {
  margin-top: 15px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.images-preview h4 {
  margin: 0 0 10px 0;
  font-size: 0.9rem;
  color: #495057;
}

.images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
}

.image-item {
  position: relative;
  border-radius: 6px;
  overflow: hidden;
  background: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.image-preview {
  width: 100%;
  height: 80px;
  object-fit: cover;
  display: block;
}

.image-info {
  padding: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.image-name {
  font-size: 0.7rem;
  color: #495057;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  margin-right: 5px;
}

.remove-image-btn {
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  cursor: pointer;
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.remove-image-btn:hover {
  background: #c82333;
  transform: scale(1.1);
}

/* Estilos para imágenes en el feed */
.feed-images {
  margin-top: 10px;
}

.feed-images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 8px;
}

.feed-image-item {
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
}

.feed-image-item:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0,0,0,0.2);
}

.feed-image {
  width: 100%;
  height: 80px;
  object-fit: cover;
  display: block;
}

/* Modal para ver imágenes */
.image-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(5px);
}

.image-modal-content {
  background: white;
  border-radius: 12px;
  max-width: 90vw;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0,0,0,0.3);
}

.image-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.image-modal-header h3 {
  margin: 0;
  font-size: 1.1rem;
  color: #495057;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  margin-right: 15px;
}

.close-modal-btn {
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  cursor: pointer;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.close-modal-btn:hover {
  background: #c82333;
  transform: scale(1.1);
}

.image-modal-body {
  padding: 20px;
  text-align: center;
}

.modal-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

/* Responsive */
@media (max-width: 768px) {
  .solutions-feed-section,
  .new-solution-section {
    margin: 15px;
    padding: 15px;
  }
  
  .feed-item {
    padding: 10px;
  }
  
  .avatar-icon {
    width: 35px;
    height: 35px;
    font-size: 1rem;
  }
  
  .feed-author {
    font-size: 0.85rem;
  }
  
  .feed-time {
    font-size: 0.75rem;
  }
  
  .feed-message {
    font-size: 0.85rem;
  }
  
  .form-actions {
    flex-direction: column;
    align-items: stretch;
  }
  
  .add-solution-btn,
  .clear-btn {
    justify-content: center;
  }
}

/* ===== MEJORAS RESPONSIVAS PARA DISPOSITIVOS MÓVILES ===== */

/* ===== OPTIMIZACIONES ESPECÍFICAS PARA MODO VERTICAL (PORTRAIT) ===== */

/* Móviles en modo vertical - anchos más estrechos */
@media (max-width: 480px) and (orientation: portrait) {
  .main-content {
    width: 100%;
    margin: 0;
    padding: 3px;
  }
  
  .ticket-form-container {
    padding: 0;
  }
  
  
  .table-header h3 {
    font-size: 1rem;
    text-align: center;
    margin-bottom: 10px;
  }
  
  .header-actions {
    gap: 6px;
  }
  
  .new-ticket-btn {
    padding: 10px 6px;
    font-size: 0.85rem;
    min-height: 44px;
  }
  
  /* Tabla optimizada para modo vertical */
  .ticket-list {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    border-radius: 6px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.1);
  }
  
  .ticket-list table {
    min-width: 600px; /* Reducido para modo vertical */
    font-size: 0.7rem;
  }
  
  .ticket-list th:nth-child(1), .ticket-list td:nth-child(1) { /* ID */
    width: 20px;
    min-width: 20px;
  }
  
  .ticket-list th:nth-child(2), .ticket-list td:nth-child(2) { /* Título */
    width: 100px;
    min-width: 80px;
  }
  
  .ticket-list th:nth-child(3), .ticket-list td:nth-child(3) { /* Solicitante */
    width: 60px;
    min-width: 50px;
  }
  
  .ticket-list th:nth-child(4), .ticket-list td:nth-child(4) { /* Área Solicitante */
    width: 60px;
    min-width: 50px;
  }
  
  .ticket-list th:nth-child(5), .ticket-list td:nth-child(5) { /* Prioridad */
    width: 50px;
    min-width: 40px;
  }
  
  .ticket-list th:nth-child(6), .ticket-list td:nth-child(6) { /* Fecha Creación */
    width: 50px;
    min-width: 40px;
    font-size: 0.6rem;
  }
  
  .ticket-list th:nth-child(7), .ticket-list td:nth-child(7) { /* Fecha Cierre */
    width: 50px;
    min-width: 40px;
    font-size: 0.6rem;
  }
  
  .ticket-list th:nth-child(8), .ticket-list td:nth-child(8) { /* Técnico */
    width: 60px;
    min-width: 50px;
  }
  
  .ticket-list th:nth-child(9), .ticket-list td:nth-child(9) { /* Estado */
    width: 50px;
    min-width: 40px;
  }
  
  /* Modales en modo vertical */
  .form-modal {
    width: 100vw;
    height: 100vh;
    margin: 0;
    border-radius: 0;
  }
  
  .modal-content {
    max-width: 100vw;
    max-height: 100vh;
    padding: 8px 5px;
    border-radius: 0;
  }
  
  .ticket-form-modal .form-row {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  
  .ticket-form-modal .submit-btn {
    width: 100%;
    padding: 12px;
    font-size: 1rem;
    min-height: 48px;
  }
  
  /* Botones optimizados para modo vertical */
  .action-btn {
    min-height: 44px;
    padding: 10px 12px;
    font-size: 0.8rem;
  }
  
  .status-badge {
    padding: 3px 6px;
    font-size: 0.65rem;
  }
}

/* Tablets en modo vertical */
@media (max-width: 768px) and (orientation: portrait) {
  .main-content {
    width: 100%;
    margin: 0;
    padding: 8px;
  }
  
  .ticket-form-container {
    padding: 3px;
  }
  
  
  .table-header {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  
  .header-actions {
    flex-direction: column;
    gap: 8px;
  }
  
  .new-ticket-btn {
    width: 100%;
    justify-content: center;
    padding: 12px 8px;
    font-size: 0.9rem;
    min-height: 46px;
  }
  
  /* Tabla con scroll horizontal para modo vertical */
  .ticket-list {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    border-radius: 6px;
    box-shadow: 0 2px 6px rgba(0,0,0,0.1);
  }
  
  .ticket-list table {
    min-width: 700px; /* Ajustado para modo vertical */
    font-size: 0.75rem;
  }
  
  .ticket-list th:nth-child(1), .ticket-list td:nth-child(1) { /* ID */
    width: 25px;
    min-width: 25px;
  }
  
  .ticket-list th:nth-child(2), .ticket-list td:nth-child(2) { /* Título */
    width: 120px;
    min-width: 100px;
  }
  
  .ticket-list th:nth-child(3), .ticket-list td:nth-child(3) { /* Solicitante */
    width: 70px;
    min-width: 60px;
  }
  
  .ticket-list th:nth-child(4), .ticket-list td:nth-child(4) { /* Área */
    width: 70px;
    min-width: 60px;
  }
  
  .ticket-list th:nth-child(5), .ticket-list td:nth-child(5) { /* Solicitud */
    width: 60px;
    min-width: 50px;
  }
  
  .ticket-list th:nth-child(6), .ticket-list td:nth-child(6) { /* Fecha Creación */
    width: 60px;
    min-width: 50px;
    font-size: 0.65rem;
  }
  
  .ticket-list th:nth-child(7), .ticket-list td:nth-child(7) { /* Fecha Cierre */
    width: 60px;
    min-width: 50px;
    font-size: 0.65rem;
  }
  
  .ticket-list th:nth-child(8), .ticket-list td:nth-child(8) { /* Técnico */
    width: 70px;
    min-width: 60px;
  }
  
  .ticket-list th:nth-child(9), .ticket-list td:nth-child(9) { /* Estado */
    width: 60px;
    min-width: 50px;
  }
  
  /* Modales en modo vertical */
  .form-modal {
    width: 100%;
    max-width: 100vw;
    max-height: 100vh;
    margin: 0;
    border-radius: 0;
  }
  
  .modal-content {
    max-width: 100vw;
    max-height: 100vh;
    padding: 12px 8px;
    border-radius: 0;
  }
  
  .ticket-form-modal .form-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .ticket-form-modal .submit-btn {
    width: 100%;
    justify-content: center;
    min-height: 46px;
  }
}

/* Tablets grandes (1025px - 1200px) */
@media (max-width: 1200px) {
  .main-content {
    width: 98%;
    max-width: 100%;
  }
  
  .form-row {
    grid-template-columns: 1fr 1fr;
  }
}

/* Tablets (769px - 1024px) */
@media (max-width: 1024px) {
  .main-content {
    width: 98%;
    max-width: 100%;
  }
  
  .ticket-list table {
    font-size: 0.85rem;
  }
  
  .ticket-list th:nth-child(1), .ticket-list td:nth-child(1) { /* ID */
    width: 35px;
    min-width: 35px;
  }
  
  .ticket-list th:nth-child(2), .ticket-list td:nth-child(2) { /* Título */
    width: 180px;
    min-width: 140px;
  }
  
  .ticket-list th:nth-child(3), .ticket-list td:nth-child(3) { /* Solicitante */
    width: 100px;
    min-width: 80px;
  }
  
  .ticket-list th:nth-child(4), .ticket-list td:nth-child(4) { /* Área */
    width: 100px;
    min-width: 80px;
  }
  
  .ticket-list th:nth-child(5), .ticket-list td:nth-child(5) { /* Solicitud */
    width: 80px;
    min-width: 70px;
  }
  
  .ticket-list th:nth-child(6), .ticket-list td:nth-child(6) { /* Fecha Creación */
    width: 80px;
    min-width: 70px;
    font-size: 0.75rem;
  }
  
  .ticket-list th:nth-child(7), .ticket-list td:nth-child(7) { /* Fecha Cierre */
    width: 80px;
    min-width: 70px;
    font-size: 0.75rem;
  }
  
  .ticket-list th:nth-child(8), .ticket-list td:nth-child(8) { /* Técnico */
    width: 100px;
    min-width: 80px;
  }
  
  .ticket-list th:nth-child(9), .ticket-list td:nth-child(9) { /* Estado */
    width: 80px;
    min-width: 70px;
  }
}

/* Tablets pequeñas y móviles grandes (481px - 768px) - MEJORADO */
@media (max-width: 768px) {
  .main-content {
    width: 100%;
    max-width: 100%;
    margin: 0;
    padding: 10px;
  }
  
  .ticket-form-container {
    padding: 5px;
  }
  
  
  .table-header {
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
  }
  
  .header-actions {
    flex-direction: column;
    gap: 10px;
  }
  
  .new-ticket-btn {
    width: 100%;
    justify-content: center;
  }
  
  /* Tabla con scroll horizontal mejorado */
  .ticket-list {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
  
  .ticket-list table {
    min-width: 800px; /* Ancho mínimo para mantener legibilidad */
    font-size: 0.8rem;
  }
  
  .ticket-list th:nth-child(1), .ticket-list td:nth-child(1) { /* ID */
    width: 30px;
    min-width: 30px;
  }
  
  .ticket-list th:nth-child(2), .ticket-list td:nth-child(2) { /* Título */
    width: 150px;
    min-width: 120px;
  }
  
  .ticket-list th:nth-child(3), .ticket-list td:nth-child(3) { /* Solicitante */
    width: 80px;
    min-width: 70px;
  }
  
  .ticket-list th:nth-child(4), .ticket-list td:nth-child(4) { /* Área */
    width: 80px;
    min-width: 70px;
  }
  
  .ticket-list th:nth-child(5), .ticket-list td:nth-child(5) { /* Solicitud */
    width: 70px;
    min-width: 60px;
  }
  
  .ticket-list th:nth-child(6), .ticket-list td:nth-child(6) { /* Fecha Creación */
    width: 70px;
    min-width: 60px;
    font-size: 0.7rem;
  }
  
  .ticket-list th:nth-child(7), .ticket-list td:nth-child(7) { /* Fecha Cierre */
    width: 70px;
    min-width: 60px;
    font-size: 0.7rem;
  }
  
  .ticket-list th:nth-child(8), .ticket-list td:nth-child(8) { /* Técnico */
    width: 80px;
    min-width: 70px;
  }
  
  .ticket-list th:nth-child(9), .ticket-list td:nth-child(9) { /* Estado */
    width: 70px;
    min-width: 60px;
  }
  
  .form-modal {
    width: 100%;
    max-width: 100vw;
    max-height: 100vh;
    margin: 0;
    border-radius: 0;
  }
  
  .modal-content {
    max-width: 100vw;
    max-height: 100vh;
    padding: 15px 10px;
    border-radius: 0;
  }
  
  .ticket-form-modal .form-row {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .ticket-form-modal .submit-btn {
    width: 100%;
    justify-content: center;
  }
}

/* Móviles pequeños (320px - 480px) */
@media (max-width: 480px) {
  .main-content {
    width: 100%;
    margin: 0;
    padding: 5px;
  }
  
  .ticket-form-container {
    padding: 0;
  }
  
  
  .table-header h3 {
    font-size: 1.1rem;
    text-align: center;
  }
  
  .header-actions {
    gap: 8px;
  }
  
  .new-ticket-btn {
    padding: 12px 8px;
    font-size: 0.9rem;
  }
  
  /* Tabla más compacta para móviles pequeños */
  .ticket-list table {
    min-width: 700px;
    font-size: 0.75rem;
  }
  
  .ticket-list th:nth-child(1), .ticket-list td:nth-child(1) { /* ID */
    width: 25px;
    min-width: 25px;
  }
  
  .ticket-list th:nth-child(2), .ticket-list td:nth-child(2) { /* Título */
    width: 120px;
    min-width: 100px;
  }
  
  .ticket-list th:nth-child(3), .ticket-list td:nth-child(3) { /* Solicitante */
    width: 70px;
    min-width: 60px;
  }
  
  .ticket-list th:nth-child(4), .ticket-list td:nth-child(4) { /* Área */
    width: 70px;
    min-width: 60px;
  }
  
  .ticket-list th:nth-child(5), .ticket-list td:nth-child(5) { /* Solicitud */
    width: 60px;
    min-width: 50px;
  }
  
  .ticket-list th:nth-child(6), .ticket-list td:nth-child(6) { /* Fecha Creación */
    width: 60px;
    min-width: 50px;
    font-size: 0.65rem;
  }
  
  .ticket-list th:nth-child(7), .ticket-list td:nth-child(7) { /* Fecha Cierre */
    width: 60px;
    min-width: 50px;
    font-size: 0.65rem;
  }
  
  .ticket-list th:nth-child(8), .ticket-list td:nth-child(8) { /* Técnico */
    width: 70px;
    min-width: 60px;
  }
  
  .ticket-list th:nth-child(9), .ticket-list td:nth-child(9) { /* Estado */
    width: 60px;
    min-width: 50px;
  }
  
  .form-modal {
    width: 100vw;
    height: 100vh;
    margin: 0;
    border-radius: 0;
  }
  
  .modal-content {
    max-width: 100vw;
    max-height: 100vh;
    padding: 10px 8px;
    border-radius: 0;
  }
  
  .ticket-form-modal .form-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .ticket-form-modal .submit-btn {
    width: 100%;
    padding: 12px;
    font-size: 1rem;
  }
  
  /* Botones más grandes para touch */
  .action-btn {
    min-height: 44px;
    padding: 12px 16px;
  }
  
  .status-badge {
    padding: 4px 8px;
    font-size: 0.7rem;
  }
}

/* Móviles muy pequeños (hasta 320px) */
@media (max-width: 320px) {
  .main-content {
    width: 100%;
    margin: 0;
    padding: 3px;
  }
  
  
  .table-header h3 {
    font-size: 1rem;
  }
  
  .new-ticket-btn {
    padding: 10px 6px;
    font-size: 0.85rem;
  }
  
  .ticket-list table {
    min-width: 650px;
    font-size: 0.7rem;
  }
  
  .ticket-list th:nth-child(1), .ticket-list td:nth-child(1) { /* ID */
    width: 20px;
    min-width: 20px;
  }
  
  .ticket-list th:nth-child(2), .ticket-list td:nth-child(2) { /* Título */
    width: 100px;
    min-width: 80px;
  }
  
  .ticket-list th:nth-child(3), .ticket-list td:nth-child(3) { /* Solicitante */
    width: 60px;
    min-width: 50px;
  }
  
  .ticket-list th:nth-child(4), .ticket-list td:nth-child(4) { /* Área Solicitante */
    width: 60px;
    min-width: 50px;
  }
  
  .ticket-list th:nth-child(5), .ticket-list td:nth-child(5) { /* Prioridad */
    width: 50px;
    min-width: 40px;
  }
  
  .ticket-list th:nth-child(6), .ticket-list td:nth-child(6) { /* Fecha Creación */
    width: 50px;
    min-width: 40px;
    font-size: 0.6rem;
  }
  
  .ticket-list th:nth-child(7), .ticket-list td:nth-child(7) { /* Fecha Cierre */
    width: 50px;
    min-width: 40px;
    font-size: 0.6rem;
  }
  
  .ticket-list th:nth-child(8), .ticket-list td:nth-child(8) { /* Técnico */
    width: 60px;
    min-width: 50px;
  }
  
  .ticket-list th:nth-child(9), .ticket-list td:nth-child(9) { /* Estado */
    width: 50px;
    min-width: 40px;
  }
  
  .modal-content {
    padding: 8px 5px;
  }
}

/* ===== OPTIMIZACIONES PARA PANTALLAS TÁCTILES ===== */

/* ===== OPTIMIZACIONES ESPECÍFICAS PARA MODO VERTICAL EN TÁCTILES ===== */

/* Controles táctiles optimizados para modo vertical */
@media (max-width: 480px) and (orientation: portrait) {
  /* Botones principales más compactos en modo vertical */
  .new-ticket-btn {
    min-height: 48px;
    padding: 12px 8px;
    font-size: 0.9rem;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  
  /* Botones de acción más pequeños para modo vertical */
  .action-btn, .detalle-btn {
    min-height: 42px;
    padding: 8px 12px;
    font-size: 0.8rem;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  
  /* Botones de estadísticas */
  .stats-btn {
    min-height: 42px;
    padding: 10px 14px;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  
  /* Botones de dropdown */
  .dropdown-menu button {
    min-height: 42px;
    padding: 10px 14px;
    font-size: 0.85rem;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  
  /* Botones de filtro */
  .clear-filter-btn {
    min-height: 42px;
    padding: 10px 14px;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  
  /* Botones de formulario */
  .submit-btn {
    min-height: 48px;
    padding: 12px 16px;
    font-size: 1rem;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  
  /* Inputs optimizados para modo vertical */
  input[type="text"], 
  input[type="email"], 
  input[type="tel"], 
  input[type="number"],
  textarea,
  select {
    min-height: 44px;
    padding: 12px 14px;
    font-size: 16px; /* Previene zoom en iOS */
    touch-action: manipulation;
  }
  
  /* Radio buttons y checkboxes */
  input[type="radio"], 
  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    margin-right: 10px;
  }
  
  /* Labels optimizados para modo vertical */
  .radio-item, .checkbox-label {
    min-height: 44px;
    padding: 10px 14px;
    display: flex;
    align-items: center;
    touch-action: manipulation;
  }
}

/* Tablets en modo vertical - controles táctiles */
@media (max-width: 768px) and (orientation: portrait) {
  /* Botones principales */
  .new-ticket-btn {
    min-height: 46px;
    padding: 12px 16px;
    font-size: 0.95rem;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  
  /* Botones de acción */
  .action-btn, .detalle-btn {
    min-height: 44px;
    padding: 10px 14px;
    font-size: 0.85rem;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  
  /* Botones de estadísticas */
  .stats-btn {
    min-height: 44px;
    padding: 12px 16px;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  
  /* Botones de dropdown */
  .dropdown-menu button {
    min-height: 44px;
    padding: 12px 16px;
    font-size: 0.9rem;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  
  /* Botones de filtro */
  .clear-filter-btn {
    min-height: 44px;
    padding: 12px 16px;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  
  /* Botones de formulario */
  .submit-btn {
    min-height: 46px;
    padding: 12px 18px;
    font-size: 1rem;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  
  /* Inputs optimizados */
  input[type="text"], 
  input[type="email"], 
  input[type="tel"], 
  input[type="number"],
  textarea,
  select {
    min-height: 44px;
    padding: 12px 16px;
    font-size: 16px;
    touch-action: manipulation;
  }
  
  /* Radio buttons y checkboxes */
  input[type="radio"], 
  input[type="checkbox"] {
    width: 20px;
    height: 20px;
    margin-right: 12px;
  }
  
  /* Labels optimizados */
  .radio-item, .checkbox-label {
    min-height: 44px;
    padding: 12px 16px;
    display: flex;
    align-items: center;
    touch-action: manipulation;
  }
}

/* Mejoras generales para botones en dispositivos táctiles */
@media (max-width: 768px) {
  /* Botones principales más grandes para touch */
  .new-ticket-btn {
    min-height: 48px;
    padding: 14px 20px;
    font-size: 1rem;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  
  /* Botones de acción en la tabla */
  .action-btn, .detalle-btn {
    min-height: 44px;
    padding: 10px 16px;
    font-size: 0.9rem;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  
  /* Botones de estadísticas */
  .stats-btn {
    min-height: 44px;
    padding: 12px 16px;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  
  /* Botones de dropdown */
  .dropdown-menu button {
    min-height: 44px;
    padding: 12px 16px;
    font-size: 0.9rem;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  
  /* Botones de filtro */
  .clear-filter-btn {
    min-height: 44px;
    padding: 12px 16px;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  
  /* Botones de formulario */
  .submit-btn {
    min-height: 48px;
    padding: 14px 20px;
    font-size: 1rem;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  
  /* Mejorar la experiencia táctil en inputs */
  input[type="text"], 
  input[type="email"], 
  input[type="tel"], 
  input[type="number"],
  textarea,
  select {
    min-height: 44px;
    padding: 12px 16px;
    font-size: 16px; /* Previene zoom en iOS */
    touch-action: manipulation;
  }
  
  /* Radio buttons y checkboxes más grandes */
  input[type="radio"], 
  input[type="checkbox"] {
    width: 20px;
    height: 20px;
    margin-right: 12px;
  }
  
  /* Labels más grandes para touch */
  .radio-item, .checkbox-label {
    min-height: 44px;
    padding: 12px 16px;
    display: flex;
    align-items: center;
    touch-action: manipulation;
  }
}

/* Optimizaciones adicionales para móviles pequeños */
@media (max-width: 480px) {
  .new-ticket-btn {
    min-height: 52px;
    padding: 16px 20px;
    font-size: 1.1rem;
  }
  
  .action-btn, .detalle-btn {
    min-height: 48px;
    padding: 12px 18px;
    font-size: 1rem;
  }
  
  .submit-btn {
    min-height: 52px;
    padding: 16px 20px;
    font-size: 1.1rem;
  }
  
  input[type="text"], 
  input[type="email"], 
  input[type="tel"], 
  input[type="number"],
  textarea,
  select {
    min-height: 48px;
    padding: 14px 18px;
    font-size: 16px;
  }
  
  input[type="radio"], 
  input[type="checkbox"] {
    width: 22px;
    height: 22px;
    margin-right: 14px;
  }
  
  .radio-item, .checkbox-label {
    min-height: 48px;
    padding: 14px 18px;
  }
}

/* Optimizaciones para móviles muy pequeños */
@media (max-width: 320px) {
  .new-ticket-btn {
    min-height: 50px;
    padding: 14px 16px;
    font-size: 1rem;
  }
  
  .action-btn, .detalle-btn {
    min-height: 46px;
    padding: 10px 14px;
    font-size: 0.9rem;
  }
  
  .submit-btn {
    min-height: 50px;
    padding: 14px 16px;
    font-size: 1rem;
  }
  
  input[type="text"], 
  input[type="email"], 
  input[type="tel"], 
  input[type="number"],
  textarea,
  select {
    min-height: 46px;
    padding: 12px 14px;
    font-size: 16px;
  }
  
  input[type="radio"], 
  input[type="checkbox"] {
    width: 20px;
    height: 20px;
    margin-right: 12px;
  }
  
  .radio-item, .checkbox-label {
    min-height: 46px;
    padding: 12px 14px;
  }
}
</style> 